// src/annihilation/fold3dKit.js
// ============================================================
//  ANNIHILATION — SHARED 3D KIT
//  The reusable bones every 3D screen imports (roadmap §4.17).
//  Hoisted out of OldFold3D.jsx so the Grove and the Fold — and
//  every future 3D screen — import ONE copy instead of pasting.
//
//  What's here (all screen-agnostic):
//    · mulberry32 / rnd / hashId   — seeded PRNG, never Math.random
//    · TEX_CACHE / canvasTex        — texture cache (mandatory)
//    · getBeingPalette re-export    — canon being colors
//    · BeingMesh / PlayerBody       — the four beings as primitives,
//                                     the Ascent worn (aura/crown)
//    · EnemyMesh                    — enemy bodies keyed to family+role
//    · FollowRig / StageRig         — the two camera archetypes
//    · Floaty / Label               — in-world DOM labels (drei Html)
//    · GroundBlob / plateStyle      — shared dressings
//
//  This file has NO world constants of its own — each screen owns
//  its UNIT and passes world-space positions in. That's what keeps
//  one kit serving a courtyard, a field, and a crypt.
// ============================================================

import { useRef, useEffect, useMemo } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { OrbitControls, Html } from "@react-three/drei";
import * as THREE from "three";
import { getBeingPalette } from "./BeingSprite";
import { TIERS } from "./stats";

export { getBeingPalette };

// ── Seeded PRNG — verbatim. NEVER Math.random() in a render path.
export function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const rnd = (seed) => mulberry32((seed * 2654435761) % 2147483647);
export function hashId(str) {
  let h = 2166136261;
  for (let i = 0; i < String(str).length; i++) { h ^= String(str).charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0);
}

// ── Texture cache — cache by key or every mount uploads canvases.
export const TEX_CACHE = new Map();
export function canvasTex(key, w, h, draw) {
  if (TEX_CACHE.has(key)) return TEX_CACHE.get(key);
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  draw(c.getContext("2d"), w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  TEX_CACHE.set(key, t);
  return t;
}

// ── Shared dressings ─────────────────────────────────────────
export const GroundBlob = ({ r = 0.45, opacity = 0.28 }) => (
  <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
    <circleGeometry args={[r, 16]} />
    <meshBasicMaterial color="#000" transparent opacity={opacity} />
  </mesh>
);

const VELLUM = "#e9ddc4";
export function plateStyle(accent, hover, small = false) {
  return {
    background: "rgba(20,16,25,0.86)",
    border: `2px solid ${accent}`,
    borderRadius: 8,
    padding: small ? "2px 8px" : "3px 11px",
    color: VELLUM,
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: small ? 11 : 13,
    fontWeight: 700,
    letterSpacing: 0.5,
    whiteSpace: "nowrap",
    boxShadow: hover ? `0 0 14px ${accent}, 0 2px 6px rgba(0,0,0,0.7)` : "0 2px 6px rgba(0,0,0,0.7)",
    textShadow: "0 0 3px #000, 0 1px 2px #000",
    transform: hover ? "scale(1.06)" : "scale(1)",
    transition: "transform 0.12s, box-shadow 0.12s",
  };
}

// A floating combat/label number anchored in 3D, styled DOM.
export function Floaty({ position, text, color = "#e86a5c" }) {
  return (
    <Html center position={position} zIndexRange={[30, 20]} style={{ pointerEvents: "none" }}>
      <div style={{
        color, fontFamily: "Georgia, serif", fontWeight: 800, fontSize: 15,
        textShadow: "0 0 4px #000, 0 1px 3px #000, 0 0 8px " + color,
        whiteSpace: "nowrap",
      }}>{text}</div>
    </Html>
  );
}

export function Label({ position, text, accent = "#c9a84c", hover = false, small = false, z = [16, 8] }) {
  return (
    <Html center position={position} zIndexRange={z} style={{ pointerEvents: "none" }}>
      <div style={plateStyle(accent, hover, small)}>{text}</div>
    </Html>
  );
}


// ────────────────────────────────────────────────────────────
// SPECTRAL TIGER BEB — the gorgeous pass (Batch 3D-7).
// Built to the card (Terror TCG STL-01): a midnight-coated
// kitten wreathed in living teal flame-stripes, golden lamp
// eyes, chest floof, a question-mark tail, and the spectral
// veil (rim aura + drifting wisps). No boxes anywhere — the
// body is spheres/capsules as anatomy, the coat is a painted
// canvas texture (cached, seeded, never Math.random).
//
// Contract unchanged: same footprint as the old body (~1.1
// long, head at strike height), so StrikeFX rakes, labels,
// the Ascent ring/crown, and the GroundBlob all still land.
// Colors here are display-only dressing over the canon
// palette (Vyren pattern — no ids, no palette-file edits).
// ────────────────────────────────────────────────────────────
const STB = {
  coat: "#101d22",      // the card's near-black midnight blue
  stripe: "#2fa393",    // living stripe teal
  stripeHi: "#7fe8d8",  // canon main as the stripe's hot core
  muzzle: "#9ec4bc",
  sock: "#8fd8ca",      // pale forepaws
  innerEar: "#0b171b",
  nose: "#c98a92",
  whisker: "#eafff8",
};

// The coat — midnight base, mottled, wrapped in tapering
// flame-stripes (leaf-shaped strokes with a hot core, like the
// card's fur language). One 512 canvas, cached, shared by every
// furred part; sphere/capsule UVs wrap it organically.
function stbFurTex() {
  return canvasTex("stb_fur_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#142329");
    base.addColorStop(0.55, "#0f1c21");
    base.addColorStop(1, "#0a1417");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(929);
    // fur mottle
    for (let i = 0; i < 1100; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(47,163,147,0.05)" : "rgba(3,9,11,0.09)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // flame-stripes: tapered leaf strokes + a brighter core pass
    const stripe = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 18; i++) {
      const x = R() * w, y = R() * h;
      const len = 70 + R() * 130, wd = 9 + R() * 11, ang = (R() - 0.5) * 1.0;
      stripe(x, y, len, wd, ang, STB.stripe, 0.85);
      stripe(x, y, len * 0.55, wd * 0.42, ang, STB.stripeHi, 0.45);
    }
  });
}

// The eyes — the card's whole face. Radial amber furnace with a
// soft slit pupil and a specular star, drawn once, glows via
// meshBasicMaterial + toneMapped={false}.
function stbEyeTex() {
  return canvasTex("stb_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 58, 4, 64, 64, 62);
    r.addColorStop(0, "#fff6d0");
    r.addColorStop(0.35, "#ffd257");
    r.addColorStop(0.7, "#e89a2e");
    r.addColorStop(0.92, "#6b3c0c");
    r.addColorStop(1, "#170f05");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    // slit pupil, soft-edged
    g.fillStyle = "rgba(14,10,6,0.92)";
    g.beginPath(); g.ellipse(64, 66, 9, 30, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,246,208,0.22)";
    g.beginPath(); g.ellipse(64, 48, 4, 9, 0, 0, 7); g.fill();
    // specular star
    g.fillStyle = "rgba(255,255,255,0.9)";
    g.beginPath(); g.arc(47, 43, 7, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.5)";
    g.beginPath(); g.arc(78, 82, 3.5, 0, 7); g.fill();
  });
}

// tail as a bead-chain along a question-mark curve (kitten tail-up).
// Relative to the tail root; radius tapers to a glow-tipped end.
const STB_TAIL = [
  [0, 0.00, -0.03, 0.075],
  [0, 0.10, -0.15, 0.068],
  [0, 0.22, -0.23, 0.060],
  [0, 0.34, -0.25, 0.053],
  [0, 0.45, -0.21, 0.046],
  [0, 0.53, -0.13, 0.040],
  [0, 0.58, -0.03, 0.034],
];

export function SpectralTigerBody({ pal }) {
  pal = pal || getBeingPalette("spectral_tiger");
  const fur = stbFurTex();
  const eye = stbEyeTex();
  const tail = useRef();
  const earL = useRef();
  const earR = useRef();
  const wisps = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (tail.current) {
      tail.current.rotation.x = Math.sin(t * 1.5) * 0.10;
      tail.current.rotation.z = Math.sin(t * 1.05 + 1.3) * 0.14;
    }
    if (earL.current) earL.current.rotation.z = 0.24 + Math.sin(t * 0.9) * 0.045;
    if (earR.current) earR.current.rotation.z = -0.24 - Math.sin(t * 0.9 + 2.1) * 0.045;
    // the veil's wisps orbit slow and breathe their opacity
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.6 + i * 2.1;
      m.position.set(Math.cos(p) * 0.55, 0.55 + Math.sin(t * 1.3 + i) * 0.18, Math.sin(p) * 0.55 + 0.05);
      m.material.opacity = 0.32 + Math.sin(t * 2 + i) * 0.18;
    });
  });

  // shared coat material props (spectral: a whisper translucent,
  // stripes ember faintly in fog)
  const coat = { map: fur, roughness: 0.95, transparent: true, opacity: 0.97, emissive: STB.stripe, emissiveIntensity: 0.06 };

  return (
    <group>
      {/* ── torso: loaf + chest floof + haunches ── */}
      <mesh position={[0, 0.42, -0.02]} scale={[0.85, 0.72, 1.42]} castShadow>
        <sphereGeometry args={[0.34, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      <mesh position={[0, 0.40, 0.40]} scale={[1, 0.92, 0.9]}>
        <sphereGeometry args={[0.24, 14, 12]} />
        <meshStandardMaterial {...coat} emissiveIntensity={0.09} />
      </mesh>
      {[-0.11, 0.11].map((ox, i) => (
        <mesh key={"fluff" + i} position={[ox, 0.30, 0.47]}>
          <sphereGeometry args={[0.12, 10, 8]} />
          <meshStandardMaterial {...coat} emissiveIntensity={0.09} />
        </mesh>
      ))}
      {[-0.17, 0.17].map((ox, i) => (
        <mesh key={"haunch" + i} position={[ox, 0.34, -0.34]} scale={[0.8, 1, 1.15]}>
          <sphereGeometry args={[0.20, 12, 10]} />
          <meshStandardMaterial {...coat} />
        </mesh>
      ))}

      {/* ── the head — big, kitten-proportioned, the card's face ── */}
      <group position={[0, 0.68, 0.52]}>
        <mesh scale={[1, 0.94, 0.9]} castShadow>
          <sphereGeometry args={[0.30, 18, 14]} />
          <meshStandardMaterial {...coat} />
        </mesh>
        {/* cheek floof */}
        {[-1, 1].map((sd) => (
          <mesh key={"cheek" + sd} position={[sd * 0.24, -0.07, 0.05]} rotation={[0, 0, sd * -1.9]}>
            <coneGeometry args={[0.10, 0.22, 7]} />
            <meshStandardMaterial {...coat} />
          </mesh>
        ))}
        {/* muzzle + nose */}
        <mesh position={[0, -0.08, 0.24]} scale={[1.15, 0.78, 0.9]}>
          <sphereGeometry args={[0.13, 12, 10]} />
          <meshStandardMaterial color={STB.muzzle} roughness={0.9} transparent opacity={0.97} />
        </mesh>
        <mesh position={[0, -0.045, 0.355]} scale={[1.2, 0.8, 0.8]}>
          <sphereGeometry args={[0.034, 8, 8]} />
          <meshStandardMaterial color={STB.nose} roughness={0.6} />
        </mesh>
        {/* eye sockets, then the golden lamps */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.135, 0.05, 0.17]} scale={[1, 1.1, 0.6]}>
              <sphereGeometry args={[0.10, 10, 8]} />
              <meshStandardMaterial color={STB.coat} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.13, 0.055, 0.272]} rotation={[0, sd * 0.26, 0]}>
              <circleGeometry args={[0.082, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ears — rounded, dark inner, ref'd for the idle twitch */}
        <group ref={earL} position={[-0.19, 0.25, -0.02]}>
          <mesh castShadow><coneGeometry args={[0.12, 0.26, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.045]}><coneGeometry args={[0.065, 0.15, 7]} /><meshStandardMaterial color={STB.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.13, 0]}><sphereGeometry args={[0.05, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        <group ref={earR} position={[0.19, 0.25, -0.02]}>
          <mesh castShadow><coneGeometry args={[0.12, 0.26, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.045]}><coneGeometry args={[0.065, 0.15, 7]} /><meshStandardMaterial color={STB.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.13, 0]}><sphereGeometry args={[0.05, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        {/* whiskers */}
        {[-1, 1].map((sd) =>
          [0, 1, 2].map((i) => (
            <mesh
              key={"wh" + sd + i}
              position={[sd * 0.20, -0.075 - i * 0.022, 0.26]}
              rotation={[0, sd * -0.45, sd * (0.10 - i * 0.11)]}
            >
              <boxGeometry args={[0.26, 0.006, 0.006]} />
              <meshBasicMaterial color={STB.whisker} transparent opacity={0.6} />
            </mesh>
          ))
        )}
      </group>

      {/* ── legs: capsule limbs, mitten paws (pale front socks) ── */}
      {[[-0.16, 0.30, true], [0.16, 0.30, true], [-0.17, -0.36, false], [0.17, -0.36, false]].map(([ox, oz, front], i) => (
        <group key={"leg" + i} position={[ox, 0, oz]}>
          <mesh position={[0, 0.18, 0]} castShadow>
            <capsuleGeometry args={[0.06, 0.16, 4, 8]} />
            <meshStandardMaterial {...coat} />
          </mesh>
          <mesh position={[0, 0.06, 0.03]} scale={[1, 0.7, 1.15]}>
            <sphereGeometry args={[0.08, 10, 8]} />
            <meshStandardMaterial color={front ? STB.sock : STB.coat} map={front ? null : fur} roughness={0.95} transparent opacity={0.97} />
          </mesh>
        </group>
      ))}

      {/* ── the tail — bead-chain question mark, glow-tipped ── */}
      <group ref={tail} position={[0, 0.46, -0.55]}>
        {STB_TAIL.map(([x, y, z, r], i) => {
          const tip = i === STB_TAIL.length - 1;
          return (
            <mesh key={"t" + i} position={[x, y, z]}>
              <sphereGeometry args={[r, 10, 8]} />
              <meshStandardMaterial
                {...coat}
                emissive={tip ? pal.glow : STB.stripe}
                emissiveIntensity={tip ? 0.9 : 0.06}
                toneMapped={!tip}
              />
            </mesh>
          );
        })}
      </group>

      {/* ── the spectral veil: rim aura + three drifting wisps ── */}
      <mesh position={[0, 0.52, 0.08]} scale={[0.92, 0.78, 1.28]}>
        <sphereGeometry args={[0.62, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.028, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 0.55, 0.45]} color={pal.glow} intensity={0.55} distance={2.6} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// SPECTRAL TIGER CHAMPION — the Ascent worn as flesh (Batch 3D-8).
// Built to the card (Terror TCG STL-04): the kitten grown lean
// and pale — a ghost-white coat broken by midnight rosettes,
// burning amber lamps in shadowed sockets, three brow marks,
// and the card's signature: living smoke tendrils pouring off
// the head and neck like spectral locks. Adolescent, hungry —
// "an insatiable appetite for their own class."
//
// Contract: same component pattern as the Beb (pal in, group
// out), footprint one head taller (~0.92 head height) — Ascent
// ring, GroundBlob, StrikeFX rakes, and labels all still land.
// Colors are display-only dressing over the canon palette.
// ────────────────────────────────────────────────────────────
const STC = {
  coat: "#c8ded7",      // the card's pale spectral white-mint
  rosette: "#16333d",   // midnight rosette spots
  browMark: "#1d4a5a",  // the three brow dots
  muzzle: "#e4f2ec",
  innerEar: "#0b171b",
  nose: "#8a5560",
  whisker: "#eafff8",
  smoke: "#dff5ef",     // the neck ring
};

// Pale coat broken by midnight rosettes — one 512 canvas, cached,
// seeded, shared by every furred part.
function stcFurTex() {
  return canvasTex("stc_fur_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#dcefe9");
    base.addColorStop(0.5, "#c4dcd5");
    base.addColorStop(1, "#a3c2bb");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(1747);
    // pale fur mottle
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(255,255,255,0.06)" : "rgba(47,163,147,0.05)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // rosettes — broken rings of dark blobs around a pale heart
    for (let i = 0; i < 26; i++) {
      const cx = R() * w, cy = R() * h, rr = 10 + R() * 14;
      const blobs = 3 + Math.floor(R() * 3);
      for (let b = 0; b < blobs; b++) {
        const a = R() * Math.PI * 2;
        g.fillStyle = "rgba(22,51,61,0.75)";
        g.beginPath();
        g.ellipse(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, 4 + R() * 5, 3 + R() * 4, a, 0, 7);
        g.fill();
      }
    }
    // a few faint teal ghost-streaks, the lineage showing through
    for (let i = 0; i < 6; i++) {
      const x = R() * w, y = R() * h;
      g.save(); g.translate(x, y); g.rotate((R() - 0.5) * 1.2);
      g.globalAlpha = 0.18; g.fillStyle = "#2fa393";
      g.beginPath();
      g.moveTo(0, -50); g.quadraticCurveTo(8, -10, 0, 50); g.quadraticCurveTo(-8, -10, 0, -50);
      g.closePath(); g.fill(); g.restore(); g.globalAlpha = 1;
    }
  });
}

// Amber furnace eyes — hotter and angrier than the kitten's, a
// narrower slit, a wider burnt rim. The card's whole menace.
function stcEyeTex() {
  return canvasTex("stc_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 58, 3, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.3, "#ffb547");
    r.addColorStop(0.62, "#e07018");
    r.addColorStop(0.88, "#571f04");
    r.addColorStop(1, "#120a04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(12,8,5,0.94)";
    g.beginPath(); g.ellipse(64, 66, 7, 32, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,242,200,0.2)";
    g.beginPath(); g.ellipse(64, 46, 3.5, 8, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(46, 42, 6, 0, 7); g.fill();
  });
}

// The twin tail — caP'n's call (Jul 21, third pass): all mane/robe
// fluff scrapped. The Champion's signature is the TAIL: two tails
// intertwined around a shared rising curve, fusing into ONE at the
// crest, then splitting back into two at the tip. Deterministic —
// two bead-strands offset from a spine by a separation that ramps
// to zero (the fuse) and reopens at the end (the split).
const STC_TWIN_TAIL = (() => {
  const N = 14;
  const strands = [[], []];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    // spine — the tail-up curve (relative to the tail root group)
    const sy = f * 0.64;
    const sz = -0.04 - Math.sin(f * Math.PI) * 0.22;
    // separation: apart & twisting → fused at the crest → split tips
    const sep =
      f < 0.55 ? 0.06 * Math.cos((f / 0.55) * (Math.PI / 2)) :
      f > 0.8 ? 0.05 * ((f - 0.8) / 0.2) : 0;
    const th = f * Math.PI * 3; // 1.5 turns of intertwine
    const fused = sep < 0.005;
    const r = 0.05 - f * 0.022 + (fused ? 0.012 : 0);
    strands[0].push([Math.cos(th) * sep, sy, sz + Math.sin(th) * sep, r]);
    strands[1].push([-Math.cos(th) * sep, sy, sz - Math.sin(th) * sep, r]);
  }
  return strands;
})();

// The smoke ring — the card's tendrils resolved (caP'n, Jul 21,
// final read): they aren't hair OR a mane, they form a constant
// RING around the neck. Ten billows on a circle, varied radii,
// riding a faint torus so the ring never breaks; the whole halo
// spins slowly and each billow breathes on its own fixed phase.
const STC_RING = (() => {
  const out = [];
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    out.push({ a, r: 0.055 + Math.sin(i * 2.4) * 0.018 });
  }
  return out;
})();

export function SpectralTigerChampionBody({ pal }) {
  pal = pal || getBeingPalette("spectral_tiger");
  const fur = stcFurTex();
  const eye = stcEyeTex();
  const tail = useRef();
  const earL = useRef();
  const earR = useRef();
  const wisps = useRef([]);
  const ring = useRef();
  const ringBillows = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (tail.current) {
      tail.current.rotation.x = Math.sin(t * 1.3) * 0.09;
      tail.current.rotation.z = Math.sin(t * 0.9 + 1.7) * 0.12;
    }
    if (earL.current) earL.current.rotation.z = 0.20 + Math.sin(t * 1.1) * 0.04;
    if (earR.current) earR.current.rotation.z = -0.20 - Math.sin(t * 1.1 + 2.4) * 0.04;
    // the smoke ring — a constant slow circulation around the neck
    if (ring.current) ring.current.rotation.y = t * 0.45;
    ringBillows.current.forEach((m, i) => {
      if (!m) return;
      const p = 1 + Math.sin(t * 1.3 + i * 1.7) * 0.16;
      m.scale.setScalar(p);
      m.material.opacity = 0.38 + Math.sin(t * 1.1 + i * 0.9) * 0.1;
    });
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.55 + i * 1.6;
      m.position.set(Math.cos(p) * 0.62, 0.62 + Math.sin(t * 1.2 + i) * 0.2, Math.sin(p) * 0.62 + 0.05);
      m.material.opacity = 0.3 + Math.sin(t * 1.8 + i) * 0.16;
    });
  });

  const coat = { map: fur, roughness: 0.95, transparent: true, opacity: 0.97, emissive: "#2fa393", emissiveIntensity: 0.04 };

  return (
    <group>
      {/* ── torso: longer, leaner loaf + chest + haunches ── */}
      <mesh position={[0, 0.55, -0.04]} scale={[0.78, 0.7, 1.52]} castShadow>
        <sphereGeometry args={[0.34, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      <mesh position={[0, 0.55, 0.44]} scale={[0.95, 0.92, 0.9]}>
        <sphereGeometry args={[0.22, 14, 12]} />
        <meshStandardMaterial {...coat} emissiveIntensity={0.07} />
      </mesh>
      {[-0.15, 0.15].map((ox, i) => (
        <mesh key={"haunch" + i} position={[ox, 0.46, -0.42]} scale={[0.8, 1, 1.15]}>
          <sphereGeometry args={[0.19, 12, 10]} />
          <meshStandardMaterial {...coat} />
        </mesh>
      ))}

      {/* ── the head — leaner skull, shadowed sockets, the marks ── */}
      <group position={[0, 0.92, 0.60]}>
        <mesh scale={[0.94, 0.96, 0.92]} castShadow>
          <sphereGeometry args={[0.27, 18, 14]} />
          <meshStandardMaterial {...coat} />
        </mesh>
        {/* the three brow marks */}
        {[[-0.07, 0.14, 0.20], [0, 0.165, 0.205], [0.07, 0.14, 0.20]].map(([x, y, z], i) => (
          <mesh key={"brow" + i} position={[x, y, z]}>
            <sphereGeometry args={[0.024, 8, 6]} />
            <meshStandardMaterial color={STC.browMark} roughness={1} emissive={pal.glow} emissiveIntensity={0.2} />
          </mesh>
        ))}
        {/* muzzle + nose */}
        <mesh position={[0, -0.08, 0.21]} scale={[1.1, 0.72, 0.9]}>
          <sphereGeometry args={[0.12, 12, 10]} />
          <meshStandardMaterial color={STC.muzzle} roughness={0.9} transparent opacity={0.97} />
        </mesh>
        <mesh position={[0, -0.05, 0.315]} scale={[1.2, 0.8, 0.8]}>
          <sphereGeometry args={[0.032, 8, 8]} />
          <meshStandardMaterial color={STC.nose} roughness={0.6} />
        </mesh>
        {/* shadowed sockets, then the amber lamps */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.125, 0.05, 0.155]} scale={[1.05, 1.15, 0.6]}>
              <sphereGeometry args={[0.095, 10, 8]} />
              <meshStandardMaterial color={STC.rosette} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.12, 0.055, 0.252]} rotation={[0, sd * 0.26, 0]}>
              <circleGeometry args={[0.075, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ears — taller, alert */}
        <group ref={earL} position={[-0.17, 0.25, -0.03]}>
          <mesh castShadow><coneGeometry args={[0.10, 0.28, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.04]}><coneGeometry args={[0.055, 0.17, 7]} /><meshStandardMaterial color={STC.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.14, 0]}><sphereGeometry args={[0.042, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        <group ref={earR} position={[0.17, 0.25, -0.03]}>
          <mesh castShadow><coneGeometry args={[0.10, 0.28, 8]} /><meshStandardMaterial {...coat} /></mesh>
          <mesh position={[0, 0.02, 0.04]}><coneGeometry args={[0.055, 0.17, 7]} /><meshStandardMaterial color={STC.innerEar} roughness={1} /></mesh>
          <mesh position={[0, 0.14, 0]}><sphereGeometry args={[0.042, 8, 6]} /><meshStandardMaterial {...coat} /></mesh>
        </group>
        {/* whiskers */}
        {[-1, 1].map((sd) =>
          [0, 1].map((i) => (
            <mesh
              key={"wh" + sd + i}
              position={[sd * 0.19, -0.07 - i * 0.025, 0.23]}
              rotation={[0, sd * -0.45, sd * (0.08 - i * 0.13)]}
            >
              <boxGeometry args={[0.26, 0.006, 0.006]} />
              <meshBasicMaterial color={STC.whisker} transparent opacity={0.55} />
            </mesh>
          ))
        )}
      </group>

      {/* ── the smoke ring — the constant halo circling the neck ── */}
      <group position={[0, 0.78, 0.48]} rotation={[-0.45, 0, 0]}>
        {/* faint continuous band so the ring never breaks */}
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.24, 0.045, 8, 24]} />
          <meshBasicMaterial color={STC.smoke} transparent opacity={0.16} depthWrite={false} toneMapped={false} />
        </mesh>
        {/* the circulating billows */}
        <group ref={ring}>
          {STC_RING.map((b, i) => (
            <mesh
              key={"ring" + i}
              ref={(el) => (ringBillows.current[i] = el)}
              position={[Math.cos(b.a) * 0.24, 0, Math.sin(b.a) * 0.24]}
            >
              <sphereGeometry args={[b.r, 8, 6]} />
              <meshBasicMaterial color={STC.smoke} transparent opacity={0.4} depthWrite={false} toneMapped={false} />
            </mesh>
          ))}
        </group>
      </group>

      {/* ── legs: longer capsules, rosetted fur down to the paws ── */}
      {[[-0.15, 0.34], [0.15, 0.34], [-0.16, -0.42], [0.16, -0.42]].map(([ox, oz], i) => (
        <group key={"leg" + i} position={[ox, 0, oz]}>
          <mesh position={[0, 0.22, 0]} castShadow>
            <capsuleGeometry args={[0.055, 0.24, 4, 8]} />
            <meshStandardMaterial {...coat} />
          </mesh>
          <mesh position={[0, 0.06, 0.03]} scale={[1, 0.7, 1.15]}>
            <sphereGeometry args={[0.075, 10, 8]} />
            <meshStandardMaterial {...coat} />
          </mesh>
        </group>
      ))}

      {/* ── the twin tail — intertwined, fused at the crest, split tips ── */}
      <group ref={tail} position={[0, 0.56, -0.66]}>
        {STC_TWIN_TAIL.map((strand, si) => (
          <group key={"strand" + si}>
            {strand.map(([x, y, z, r], i, arr) => {
              const tip = i === arr.length - 1;
              return (
                <mesh key={"t" + i} position={[x, y, z]}>
                  <sphereGeometry args={[r, 10, 8]} />
                  <meshStandardMaterial
                    {...coat}
                    emissive={tip ? pal.glow : "#2fa393"}
                    emissiveIntensity={tip ? 0.9 : 0.05}
                    toneMapped={!tip}
                  />
                </mesh>
              );
            })}
          </group>
        ))}
      </group>

      {/* ── the spectral veil: pale rim aura + four wisps ── */}
      <mesh position={[0, 0.62, 0.06]} scale={[0.92, 0.82, 1.32]}>
        <sphereGeometry args={[0.66, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.08} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.026, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as the Beb) */}
      <pointLight position={[0, 0.65, 0.45]} color={pal.glow} intensity={0.55} distance={2.8} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// SPECTRAL TIGER LORD — the Ascent completed (Batch 3D-8).
// Built to the card (Terror TCG STL-07): King of the Spirit
// Worlds. A seated regal mass in a deep flowing coat, crowned
// by a radiating fan of spectral plumes; SIX eyes — the golden
// pair, two vast outer orbs set in dark sockets, and the third
// eye burning on the brow; white cheek ruffs and long whiskers;
// forelegs that dissolve into pooling smoke where paws should
// be. It does not prowl. It presides.
//
// Contract: seated footprint inside the Beb/Champion disc; head
// at ~1.08 so the worn Lord crown (PlayerBody, y=1.5) lands at
// the mane's crest, framed by the fan. One pointLight.
// ────────────────────────────────────────────────────────────
const STL = {
  coat: "#16323e",      // the card's deep spirit-blue
  ruff: "#e8f4ee",      // white cheek fur
  muzzle: "#dcece6",
  nose: "#8a5560",
  whisker: "#eafff8",
  smoke: "#dff5ef",
  socket: "#0a1418",    // the dark orb sockets
};

// Deep flowing coat — long vertical spirit-streaks over midnight
// blue, pale cores; the card's living-current fur.
function stlFurTex() {
  return canvasTex("stl_fur_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#1b3d4a");
    base.addColorStop(0.5, "#122a34");
    base.addColorStop(1, "#0b1b22");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(3121);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(47,163,147,0.06)" : "rgba(3,9,11,0.09)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    const streak = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 22; i++) {
      const x = R() * w, y = R() * h;
      const len = 120 + R() * 90, wd = 10 + R() * 8, ang = (R() - 0.5) * 0.5;
      streak(x, y, len, wd, ang, "#2c7f8f", 0.5);
      streak(x, y, len * 0.55, wd * 0.4, ang, "#9fdcea", 0.3);
    }
    for (let i = 0; i < 5; i++) {
      const x = R() * w, y = R() * h;
      streak(x, y, 90 + R() * 60, 6 + R() * 4, (R() - 0.5) * 0.5, "#eafff8", 0.22);
    }
  });
}

// The golden regard — round pupil, serene. Shared by the main
// pair and the vast outer orbs.
function stlEyeTex() {
  return canvasTex("stl_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff8d8");
    r.addColorStop(0.35, "#ffd257");
    r.addColorStop(0.7, "#d98d1e");
    r.addColorStop(0.92, "#5a350a");
    r.addColorStop(1, "#120c04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,10,6,0.9)";
    g.beginPath(); g.arc(64, 66, 13, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(48, 44, 6, 0, 7); g.fill();
  });
}

// The third eye — the golden regard ringed in spirit-teal.
function stlThirdEyeTex() {
  return canvasTex("stl_eye3_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 62, 3, 64, 64, 62);
    r.addColorStop(0, "#fff8d8");
    r.addColorStop(0.3, "#ffd257");
    r.addColorStop(0.58, "#d98d1e");
    r.addColorStop(0.78, "#1c5a66");
    r.addColorStop(0.92, "#7fe8d8");
    r.addColorStop(1, "#0a1418");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,10,6,0.9)";
    g.beginPath(); g.ellipse(64, 65, 8, 16, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.8)";
    g.beginPath(); g.arc(52, 48, 4.5, 0, 7); g.fill();
  });
}

// The mane fan — two arcs of plumes, computed deterministically.
// Outer ring of nine long plumes, inner ring of seven bright ones.
const STL_PLUMES = (() => {
  const out = [];
  for (let i = 0; i < 9; i++) {
    const a = -1.75 + (3.5 * i) / 8;
    out.push({ a, len: 0.62 - Math.abs(a) * 0.10, r: 0.10, ring: 0 });
  }
  for (let i = 0; i < 7; i++) {
    const a = -1.35 + (2.7 * i) / 6;
    out.push({ a, len: 0.42 - Math.abs(a) * 0.06, r: 0.075, ring: 1 });
  }
  return out;
})();

// Base smoke curls — two bead-chains coiling off the seated mass.
const STL_CURLS = [
  [[-0.30, 0.16, -0.34], [-0.40, 0.10, -0.44], [-0.49, 0.06, -0.52], [-0.55, 0.04, -0.58], [-0.58, 0.03, -0.62]],
  [[0.30, 0.16, -0.34], [0.40, 0.10, -0.44], [0.49, 0.06, -0.52], [0.55, 0.04, -0.58], [0.58, 0.03, -0.62]],
];

export function SpectralTigerLordBody({ pal }) {
  pal = pal || getBeingPalette("spectral_tiger");
  const fur = stlFurTex();
  const eye = stlEyeTex();
  const eye3 = stlThirdEyeTex();
  const plumes = useRef([]);
  const wisps = useRef([]);
  const paws = useRef([]);
  const third = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the mane breathes — each plume sways on its own fixed phase
    plumes.current.forEach((grp, i) => {
      if (!grp) return;
      grp.rotation.z = -STL_PLUMES[i].a + Math.sin(t * 0.7 + i * 0.9) * 0.04;
    });
    // the smoke paws pool and breathe
    paws.current.forEach((m, i) => {
      if (!m) return;
      m.material.opacity = 0.55 + Math.sin(t * 1.4 + i * 1.1) * 0.15;
    });
    // the third eye's slow pulse
    if (third.current) {
      const p = 1 + Math.sin(t * 1.1) * 0.05;
      third.current.scale.set(p, p, 1);
    }
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.45 + i * 1.3;
      m.position.set(Math.cos(p) * 0.8, 0.75 + Math.sin(t * 1.1 + i) * 0.24, Math.sin(p) * 0.8);
      m.material.opacity = 0.28 + Math.sin(t * 1.6 + i) * 0.14;
    });
  });

  const coat = { map: fur, roughness: 0.95, transparent: true, opacity: 0.97, emissive: "#2c7f8f", emissiveIntensity: 0.06 };
  const smokeMat = { color: STL.smoke, roughness: 1, transparent: true, opacity: 0.65, emissive: pal.glow, emissiveIntensity: 0.22 };

  return (
    <group>
      {/* ── the seated mass: haunches + upright chest ── */}
      <mesh position={[0, 0.30, -0.06]} scale={[1.25, 0.72, 1.2]} castShadow>
        <sphereGeometry args={[0.40, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      <mesh position={[0, 0.60, 0.06]} scale={[1.0, 0.95, 1.05]} castShadow>
        <sphereGeometry args={[0.42, 18, 14]} />
        <meshStandardMaterial {...coat} />
      </mesh>
      {/* chest ruff */}
      <mesh position={[0, 0.66, 0.34]} scale={[0.95, 1.05, 0.85]}>
        <sphereGeometry args={[0.24, 14, 12]} />
        <meshStandardMaterial color={STL.ruff} roughness={1} transparent opacity={0.92} emissive={pal.glow} emissiveIntensity={0.05} />
      </mesh>

      {/* ── the head — broad regal skull, six eyes, white ruffs ── */}
      <group position={[0, 1.08, 0.28]}>
        <mesh scale={[1.05, 0.95, 0.92]} castShadow>
          <sphereGeometry args={[0.30, 18, 14]} />
          <meshStandardMaterial {...coat} />
        </mesh>
        {/* white cheek ruffs — three flares per side */}
        {[-1, 1].map((sd) =>
          [0, 1, 2].map((k) => (
            <mesh
              key={"ruff" + sd + k}
              position={[sd * (0.26 + k * 0.015), -0.06 + k * 0.06, 0.09]}
              rotation={[0, 0, sd * (-1.55 - k * 0.28)]}
            >
              <coneGeometry args={[0.085, 0.26, 7]} />
              <meshStandardMaterial color={STL.ruff} roughness={1} transparent opacity={0.9} />
            </mesh>
          ))
        )}
        {/* muzzle + nose */}
        <mesh position={[0, -0.09, 0.24]} scale={[1.3, 0.75, 0.9]}>
          <sphereGeometry args={[0.14, 12, 10]} />
          <meshStandardMaterial color={STL.muzzle} roughness={0.9} transparent opacity={0.96} />
        </mesh>
        <mesh position={[0, -0.055, 0.36]} scale={[1.25, 0.85, 0.8]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color={STL.nose} roughness={0.6} />
        </mesh>
        {/* the golden pair */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.135, 0.05, 0.17]} scale={[1, 1.1, 0.6]}>
              <sphereGeometry args={[0.10, 10, 8]} />
              <meshStandardMaterial color={STL.socket} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.13, 0.055, 0.272]} rotation={[0, sd * 0.22, 0]}>
              <circleGeometry args={[0.082, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* the third eye, on the brow */}
        <group position={[0, 0.185, 0.20]}>
          <mesh scale={[1, 1.1, 0.6]}>
            <sphereGeometry args={[0.065, 10, 8]} />
            <meshStandardMaterial color={STL.socket} roughness={1} />
          </mesh>
          <mesh ref={third} position={[0, 0.004, 0.045]} rotation={[-0.22, 0, 0]}>
            <circleGeometry args={[0.05, 18]} />
            <meshBasicMaterial map={eye3} toneMapped={false} />
          </mesh>
        </group>
        {/* the vast outer orbs, set wide in dark sockets */}
        {[-1, 1].map((sd) => (
          <group key={"orb" + sd} position={[sd * 0.33, 0.10, 0.02]}>
            <mesh scale={[1, 1.05, 0.85]}>
              <sphereGeometry args={[0.105, 12, 10]} />
              <meshStandardMaterial color={STL.socket} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.055, 0.005, 0.055]} rotation={[0, sd * 0.85, 0]}>
              <circleGeometry args={[0.072, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ears — half-lost in the mane */}
        {[-1, 1].map((sd) => (
          <mesh key={"ear" + sd} position={[sd * 0.19, 0.26, -0.06]} rotation={[0, 0, sd * -0.2]}>
            <coneGeometry args={[0.09, 0.18, 7]} />
            <meshStandardMaterial {...coat} />
          </mesh>
        ))}
        {/* long whiskers */}
        {[-1, 1].map((sd) =>
          [0, 1, 2].map((i) => (
            <mesh
              key={"wh" + sd + i}
              position={[sd * 0.24, -0.08 - i * 0.024, 0.26]}
              rotation={[0, sd * -0.4, sd * (0.12 - i * 0.12)]}
            >
              <boxGeometry args={[0.36, 0.006, 0.006]} />
              <meshBasicMaterial color={STL.whisker} transparent opacity={0.6} />
            </mesh>
          ))
        )}
        {/* ── the mane fan — the crown of the Spirit Worlds ── */}
        {STL_PLUMES.map((p, i) => (
          <group
            key={"plume" + i}
            ref={(el) => (plumes.current[i] = el)}
            position={[
              Math.sin(p.a) * (p.ring ? 0.24 : 0.30),
              Math.cos(p.a) * (p.ring ? 0.20 : 0.24) + 0.02,
              -0.16 - p.ring * 0.07,
            ]}
            rotation={[0.12, 0, -p.a]}
          >
            <mesh position={[0, p.len / 2 - 0.04, 0]}>
              <coneGeometry args={[p.r, p.len, 7]} />
              <meshStandardMaterial {...coat} emissiveIntensity={p.ring ? 0.2 : 0.1} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ── forelegs dissolving into pooled smoke paws ── */}
      {[-1, 1].map((sd, sideIdx) => (
        <group key={"paw" + sd}>
          <mesh position={[sd * 0.21, 0.36, 0.40]} rotation={[0.32, 0, sd * 0.06]} castShadow>
            <capsuleGeometry args={[0.075, 0.2, 4, 8]} />
            <meshStandardMaterial {...coat} opacity={0.85} />
          </mesh>
          {[[0.14, 0.09], [0.07, 0.07], [0.04, 0.055]].map(([py, pr], bi) => (
            <mesh
              key={"pw" + bi}
              ref={(el) => (paws.current[sideIdx * 3 + bi] = el)}
              position={[sd * (0.22 + bi * 0.03), py, 0.47 + bi * 0.04]}
            >
              <sphereGeometry args={[pr, 10, 8]} />
              <meshStandardMaterial {...smokeMat} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── base smoke curls coiling off the seated mass ── */}
      {STL_CURLS.map((path, ci) => (
        <group key={"curl" + ci}>
          {path.map(([x, y, z], bi) => (
            <mesh key={"cb" + bi} position={[x, y, z]}>
              <sphereGeometry args={[Math.max(0.02, 0.05 - bi * 0.007), 8, 6]} />
              <meshStandardMaterial {...smokeMat} opacity={0.5} emissiveIntensity={0.12} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the veil, widened to a king's measure ── */}
      <mesh position={[0, 0.72, 0.02]} scale={[1.05, 0.95, 1.15]}>
        <sphereGeometry args={[0.85, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.09} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.34} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as every tiger) */}
      <pointLight position={[0, 0.8, 0.4]} color={pal.glow} intensity={0.7} distance={3.2} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// THE DRAGON WING — the house wing primitive (Batch 3D-9 r2).
// Built flat in local XY (leading-edge bead-chain up-and-out,
// three finger capsules fanning from the wrist, two flattened
// membrane sails between), then mirrored per side by group
// scale and posed per tier by rotation. caP'n's call: every
// Chaos Dragon tier flies.
// ────────────────────────────────────────────────────────────
const CD_WING_EDGE = [
  [0, 0, 0.052],
  [0.11, 0.13, 0.046],
  [0.22, 0.27, 0.040],
  [0.32, 0.42, 0.033],
];
const CD_WING_FINGERS = [
  { ang: -0.25, len: 0.44 },
  { ang: -0.72, len: 0.40 },
  { ang: -1.15, len: 0.33 },
];

function CDWing({ boneMat, membrane, mOpacity = 0.5, glow }) {
  return (
    <group>
      {/* leading edge — bead-chain up and out */}
      {CD_WING_EDGE.map(([x, y, r], i) => (
        <mesh key={"e" + i} position={[x, y, 0]} castShadow={i === 0}>
          <sphereGeometry args={[r, 10, 8]} />
          <meshStandardMaterial {...boneMat} />
        </mesh>
      ))}
      {/* fingers fanning from the wrist */}
      {CD_WING_FINGERS.map((f, i) => (
        <group key={"f" + i} position={[0.32, 0.42, 0]} rotation={[0, 0, f.ang - Math.PI / 2]}>
          <mesh position={[0, f.len / 2, 0]}>
            <capsuleGeometry args={[0.016, f.len, 4, 6]} />
            <meshStandardMaterial {...boneMat} />
          </mesh>
        </group>
      ))}
      {/* membrane sails — thin flattened spheres, double-sided */}
      <mesh position={[0.42, 0.20, 0]} rotation={[0, 0, 0.5]} scale={[0.40, 0.30, 0.05]}>
        <sphereGeometry args={[1, 14, 12]} />
        <meshStandardMaterial color={membrane} roughness={0.7} transparent opacity={mOpacity} side={THREE.DoubleSide} emissive={glow} emissiveIntensity={0.1} />
      </mesh>
      <mesh position={[0.56, 0.02, 0]} rotation={[0, 0, 0.35]} scale={[0.30, 0.22, 0.045]}>
        <sphereGeometry args={[1, 14, 12]} />
        <meshStandardMaterial color={membrane} roughness={0.7} transparent opacity={mOpacity * 0.85} side={THREE.DoubleSide} emissive={glow} emissiveIntensity={0.08} />
      </mesh>
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// CHAOS DRAGON BEB — the gorgeous pass (Batch 3D-9, round 2).
// Built to the card (Terror TCG CDL-01): "The One & Only Leader
// of the Unstable World" — a pale porcelain hatchling with vast
// obsidian doll-eyes rimmed in weeping red, a radiating crown of
// bone spikes tipped in flame, a teal leaf-scale beard pouring
// down the chest, forelimbs of living golden fire trailing a
// flame spiral, and — per caP'n — new-hatched wings, half-open.
// Round 2: cheek balls cut, muzzle wedged reptilian, nostrils
// added (more Dragon, less fox), wings on.
//
// Contract unchanged: head at the old crimson box-dragon's
// strike height (~y 0.95, muzzle forward) so the jaw-snap flare
// ([0, 0.95, 0.9]), labels, Ascent ring, and GroundBlob all
// still land. One pointLight, same as the old body. Colors are
// display-only dressing over the canon palette (Vyren pattern).
// ────────────────────────────────────────────────────────────
const CDB = {
  skin: "#ded6c6",      // the card's pale porcelain hide
  skinDeep: "#b9ad9a",
  crown: "#e8dfc9",     // bone spike-crown
  flameTip: "#e84a3a",  // the crown's red flame tips (canon main)
  beard: "#2e7d8c",     // teal leaf-scale beard
  beardHi: "#5fc4c0",
  eyeRim: "#8c1f1a",    // the weeping red rims
  nostril: "#5a4638",
  membrane: "#f0c9a0",  // hatchling wing sail, fire-warmed
  flame: "#ffd08a",     // living golden fire (canon accent)
};

// The hide — porcelain base, seeded mottle in rose and faint
// teal, soft scale arcs. One 512 canvas, cached, shared by every
// hided part; sphere/capsule UVs wrap it organically.
function cdbSkinTex() {
  return canvasTex("cdb_skin_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#e9e2d3");
    base.addColorStop(0.55, "#dbd2c0");
    base.addColorStop(1, "#c4b8a2");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(4451);
    // porcelain mottle — rose flush and faint teal, the card's skin
    for (let i = 0; i < 1000; i++) {
      g.fillStyle = R() > 0.6 ? "rgba(200,120,110,0.05)" : R() > 0.3 ? "rgba(46,125,140,0.04)" : "rgba(90,78,60,0.06)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // soft scale arcs — a hatchling's first scales, barely there
    g.strokeStyle = "rgba(120,105,84,0.14)"; g.lineWidth = 1.5;
    for (let i = 0; i < 90; i++) {
      const x = R() * w, y = R() * h, r = 6 + R() * 9;
      g.beginPath(); g.arc(x, y, r, Math.PI * 0.15, Math.PI * 0.85); g.stroke();
    }
  });
}

// The obsidian doll-eyes — the card's whole face. Near-black
// depths ringed in weeping red, a vast soft specular star.
function cdbEyeTex() {
  return canvasTex("cdb_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 3, 64, 64, 62);
    r.addColorStop(0, "#2a2030");
    r.addColorStop(0.45, "#171019");
    r.addColorStop(0.78, "#0c080d");
    r.addColorStop(0.9, "#8c1f1a");
    r.addColorStop(1, "#3a0c08");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    // the vast doll-glass speculars
    g.fillStyle = "rgba(255,250,240,0.9)";
    g.beginPath(); g.arc(45, 42, 10, 0, 7); g.fill();
    g.fillStyle = "rgba(255,250,240,0.4)";
    g.beginPath(); g.arc(82, 80, 5, 0, 7); g.fill();
    g.fillStyle = "rgba(232,74,58,0.35)";
    g.beginPath(); g.arc(64, 108, 12, 0, 7); g.fill();
  });
}

// The spike-crown — a radiating halo of bone plumes around the
// skull, flame-tipped, computed deterministically (fan pattern).
const CDB_CROWN = (() => {
  const out = [];
  for (let i = 0; i < 9; i++) {
    const a = -1.85 + (3.7 * i) / 8;
    out.push({ a, len: 0.40 - Math.abs(a) * 0.055, r: 0.045, ring: 0 });
  }
  for (let i = 0; i < 6; i++) {
    const a = -1.3 + (2.6 * i) / 5;
    out.push({ a, len: 0.24 - Math.abs(a) * 0.03, r: 0.035, ring: 1 });
  }
  return out;
})();

// The flame spiral at its side — the card's golden swirl as a
// bead-chain rising around a coil (seedless, hand-tuned curve).
const CDB_SWIRL = (() => {
  const out = [];
  for (let i = 0; i < 10; i++) {
    const f = i / 9;
    const a = f * Math.PI * 2.4;
    out.push([
      0.42 + Math.cos(a) * (0.13 - f * 0.05),
      0.22 + f * 0.62,
      0.18 + Math.sin(a) * (0.13 - f * 0.05),
      0.062 - f * 0.038,
    ]);
  }
  return out;
})();

export function ChaosDragonBody({ pal }) {
  pal = pal || getBeingPalette("chaos_dragon");
  const skin = cdbSkinTex();
  const eye = cdbEyeTex();
  const crown = useRef([]);
  const swirl = useRef([]);
  const arms = useRef([]);
  const tail = useRef();
  const wingL = useRef();
  const wingR = useRef();
  const embers = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the crown breathes — each spike sways on its own fixed phase
    crown.current.forEach((grp, i) => {
      if (!grp) return;
      grp.rotation.z = -CDB_CROWN[i].a + Math.sin(t * 0.8 + i * 1.1) * 0.035;
    });
    // the hatchling wings test the air — small, eager beats
    if (wingL.current) wingL.current.rotation.z = 0.35 + Math.sin(t * 1.6) * 0.09;
    if (wingR.current) wingR.current.rotation.z = 0.35 + Math.sin(t * 1.6 + 0.4) * 0.09;
    // the flame spiral licks upward, each bead breathing
    swirl.current.forEach((m, i) => {
      if (!m) return;
      const p = 1 + Math.sin(t * 2.2 + i * 0.8) * 0.2;
      m.scale.setScalar(p);
      m.material.opacity = 0.55 + Math.sin(t * 1.7 + i * 1.2) * 0.2;
    });
    // the fire-arms flicker
    arms.current.forEach((m, i) => {
      if (!m) return;
      m.material.emissiveIntensity = 0.7 + Math.sin(t * 3.1 + i * 2.2) * 0.25;
    });
    if (tail.current) {
      tail.current.rotation.x = Math.sin(t * 1.3) * 0.08;
      tail.current.rotation.z = Math.sin(t * 0.95 + 1.1) * 0.1;
    }
    embers.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.55 + i * 2.0;
      m.position.set(Math.cos(p) * 0.58, 0.6 + Math.sin(t * 1.4 + i) * 0.2, Math.sin(p) * 0.58 + 0.1);
      m.material.opacity = 0.34 + Math.sin(t * 2.1 + i) * 0.18;
    });
  });

  const hide = { map: skin, roughness: 0.92 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the seated hatchling: round haunch + upright chest ── */}
      <mesh position={[0, 0.34, -0.08]} scale={[1.1, 0.78, 1.05]} castShadow>
        <sphereGeometry args={[0.34, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>
      <mesh position={[0, 0.62, 0.08]} scale={[0.92, 0.98, 0.9]} castShadow>
        <sphereGeometry args={[0.32, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>

      {/* ── the teal leaf-scale beard, pouring down the chest ── */}
      {[0, 1, 2].map((row) =>
        Array.from({ length: 3 + row }, (_, k) => {
          const n = 3 + row;
          const x = (k - (n - 1) / 2) * (0.11 + row * 0.015);
          return (
            <mesh
              key={"leaf" + row + k}
              position={[x, 0.86 - row * 0.14, 0.30 + row * 0.035]}
              rotation={[0.5 + row * 0.12, 0, 0]}
            >
              <coneGeometry args={[0.055 - row * 0.006, 0.16, 6]} />
              <meshStandardMaterial
                color={row === 0 ? CDB.beardHi : CDB.beard}
                roughness={0.85}
                emissive={CDB.beardHi}
                emissiveIntensity={0.08}
              />
            </mesh>
          );
        })
      )}

      {/* ── the head — vast, round, the card's porcelain face ── */}
      <group position={[0, 0.95, 0.30]}>
        <mesh scale={[1.02, 0.96, 0.9]} castShadow>
          <sphereGeometry args={[0.28, 18, 14]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* reptilian wedge muzzle + nostrils (round 2: the fox is out) */}
        <mesh position={[0, -0.075, 0.22]} scale={[0.85, 0.58, 1.05]}>
          <sphereGeometry args={[0.10, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"nos" + sd} position={[sd * 0.035, -0.05, 0.315]}>
            <sphereGeometry args={[0.014, 6, 6]} />
            <meshStandardMaterial color={CDB.nostril} roughness={1} />
          </mesh>
        ))}
        {/* the obsidian doll-eyes — huge, red-rimmed sockets */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.125, 0.045, 0.15]} scale={[1.1, 1.2, 0.6]}>
              <sphereGeometry args={[0.105, 10, 8]} />
              <meshStandardMaterial color={CDB.eyeRim} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.12, 0.05, 0.252]} rotation={[0, sd * 0.24, 0]}>
              <circleGeometry args={[0.095, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* the twin red brow-flames above the eyes */}
        {[-1, 1].map((sd) => (
          <mesh key={"brow" + sd} position={[sd * 0.13, 0.20, 0.14]} rotation={[-0.3, 0, sd * -0.5]}>
            <coneGeometry args={[0.035, 0.14, 6]} />
            <meshStandardMaterial color={CDB.flameTip} emissive={CDB.flameTip} emissiveIntensity={0.5} roughness={0.7} />
          </mesh>
        ))}
        {/* ── the spike-crown — bone halo, flame-tipped ── */}
        {CDB_CROWN.map((p, i) => (
          <group
            key={"spike" + i}
            ref={(el) => (crown.current[i] = el)}
            position={[
              Math.sin(p.a) * (p.ring ? 0.20 : 0.25),
              Math.cos(p.a) * (p.ring ? 0.17 : 0.21) + 0.03,
              -0.10 - p.ring * 0.06,
            ]}
            rotation={[0.1, 0, -p.a]}
          >
            <mesh position={[0, p.len / 2 - 0.03, 0]}>
              <coneGeometry args={[p.r, p.len, 6]} />
              <meshStandardMaterial {...hide} />
            </mesh>
            <mesh position={[0, p.len - 0.02, 0]}>
              <coneGeometry args={[p.r * 0.55, p.len * 0.28, 6]} />
              <meshStandardMaterial color={CDB.flameTip} emissive={pal.glow} emissiveIntensity={0.6} toneMapped={false} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ── the new-hatched wings, half-open, testing the air ── */}
      {[-1, 1].map((sd) => (
        <group key={"wing" + sd} position={[sd * 0.20, 0.66, -0.16]} scale={[sd * 0.6, 0.6, 0.6]} rotation={[0.25, sd * -0.35, 0]}>
          <group ref={sd < 0 ? wingL : wingR}>
            <CDWing boneMat={hide} membrane={CDB.membrane} mOpacity={0.55} glow={pal.glow} />
          </group>
        </group>
      ))}

      {/* ── forelimbs of living golden fire ── */}
      {[-1, 1].map((sd, si) => (
        <group key={"arm" + sd}>
          <mesh
            ref={(el) => (arms.current[si] = el)}
            position={[sd * 0.26, 0.44, 0.26]}
            rotation={[0.5, 0, sd * 0.35]}
            castShadow
          >
            <capsuleGeometry args={[0.055, 0.2, 4, 8]} />
            <meshStandardMaterial color={CDB.flame} emissive={pal.glow} emissiveIntensity={0.8} roughness={0.5} toneMapped={false} />
          </mesh>
          <mesh position={[sd * 0.30, 0.26, 0.36]} rotation={[0.9, 0, sd * 0.3]}>
            <coneGeometry args={[0.05, 0.14, 6]} />
            <meshBasicMaterial color={CDB.flame} transparent opacity={0.85} toneMapped={false} depthWrite={false} />
          </mesh>
        </group>
      ))}

      {/* ── stubby hind feet ── */}
      {[-0.16, 0.16].map((ox, i) => (
        <mesh key={"foot" + i} position={[ox, 0.08, 0.18]} scale={[1, 0.65, 1.2]}>
          <sphereGeometry args={[0.09, 10, 8]} />
          <meshStandardMaterial {...hide} />
        </mesh>
      ))}

      {/* ── the tail — a small curled bead-chain, ember-tipped ── */}
      <group ref={tail} position={[0, 0.30, -0.42]}>
        {[[0, 0, -0.02, 0.07], [0, 0.05, -0.13, 0.06], [0.04, 0.13, -0.2, 0.05], [0.1, 0.22, -0.22, 0.042], [0.16, 0.3, -0.2, 0.034]].map(([x, y, z, r], i, arr) => {
          const tip = i === arr.length - 1;
          return (
            <mesh key={"t" + i} position={[x, y, z]}>
              <sphereGeometry args={[r, 10, 8]} />
              <meshStandardMaterial
                {...hide}
                emissive={tip ? pal.glow : "#000000"}
                emissiveIntensity={tip ? 0.9 : 0}
                toneMapped={!tip}
              />
            </mesh>
          );
        })}
      </group>

      {/* ── the flame spiral at its side — the card's golden swirl ── */}
      {CDB_SWIRL.map(([x, y, z, r], i) => (
        <mesh key={"sw" + i} ref={(el) => (swirl.current[i] = el)} position={[x, y, z]}>
          <sphereGeometry args={[r, 8, 6]} />
          <meshBasicMaterial color={CDB.flame} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* ── the warm veil: rim aura + three ember motes ── */}
      <mesh position={[0, 0.56, 0.06]} scale={[0.95, 0.85, 1.15]}>
        <sphereGeometry args={[0.62, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2].map((i) => (
        <mesh key={"ember" + i} ref={(el) => (embers.current[i] = el)}>
          <sphereGeometry args={[0.026, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 1.0, 0.6]} color={pal.glow} intensity={0.8} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// CHAOS DRAGON CHAMPION — the Ascent worn as flesh (Batch 3D-9,
// round 2 — REBUILT on caP'n's read: too dog, too quadruped).
// Built to the card (Terror TCG CDL-04): the innocent typhoon,
// grown — and the card never showed a beast on all fours. It
// shows a PRESENCE: the burning candle-crest, the vast gilded
// blade-ears, the radiating whisker-spines, and beneath the
// head a dark feathered mass. Round 2 reads that mass as what
// it is — a ROBE. The Champion stands on hind legs, robed in
// dark feathers to the ground, clawed feet at the hem, slim
// scaled arms, tall folded wings rising behind, snout humbled
// to a reptilian wedge. The twin golden suns stay.
//
// Contract: head at the strike height (~y 1.02) so the jaw-snap
// flare, labels, Ascent ring, and GroundBlob all still land.
// One pointLight.
// ────────────────────────────────────────────────────────────
const CDC = {
  scale: "#4a7d64",     // verdigris hide
  scaleDeep: "#22392e",
  blade: "#8aa06a",     // the great blade-ears
  bladeEdge: "#ffd08a", // gilded edges (canon accent)
  robe: "#141d18",      // the dark feather robe
  robeHi: "#26362c",
  whisker: "#e8d8a8",   // pale gold spines
  flameCore: "#fff2c8", // the candle's heart
  disc: "#ffd08a",      // the twin suns
  membrane: "#b8a668",  // wing sail, old gold-green
  nostril: "#16241d",
};

// Verdigris hide — scale-row strokes over deep green, gold
// flecks catching light. Cached, seeded, shared.
function cdcScaleTex() {
  return canvasTex("cdc_scale_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#5a8f74");
    base.addColorStop(0.5, "#44735c");
    base.addColorStop(1, "#2c4a3e");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(7717);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(20,32,26,0.08)" : "rgba(160,200,150,0.05)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // scale rows — staggered arcs
    g.lineWidth = 1.6;
    for (let row = 0; row < 20; row++) {
      const y = (row / 20) * h;
      for (let cx = ((row % 2) * 13); cx < w; cx += 26) {
        g.strokeStyle = "rgba(16,28,22,0.35)";
        g.beginPath(); g.arc(cx, y, 11, Math.PI * 0.1, Math.PI * 0.9); g.stroke();
      }
    }
    // gold flecks — the gilding
    for (let i = 0; i < 60; i++) {
      g.fillStyle = "rgba(255,208,138,0.28)";
      const x = R() * w, y = R() * h;
      g.beginPath(); g.arc(x, y, 1.2 + R() * 2, 0, 7); g.fill();
    }
  });
}

// The robe — dark feathers falling in overlapping rows, faint
// gold catch-light at the barbs. Cached, seeded.
function cdcRobeTex() {
  return canvasTex("cdc_robe_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#1d2a22");
    base.addColorStop(0.5, "#141d18");
    base.addColorStop(1, "#0b110d");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(5527);
    // feather rows — downward tapered strokes, staggered
    const feather = (x, y, len, wd, fill, alpha) => {
      g.save(); g.translate(x, y);
      g.beginPath();
      g.moveTo(0, 0);
      g.quadraticCurveTo(wd, len * 0.4, 0, len);
      g.quadraticCurveTo(-wd, len * 0.4, 0, 0);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let row = 0; row < 12; row++) {
      const y = (row / 12) * h - 10;
      for (let k = 0; k < 14; k++) {
        const x = ((k + (row % 2) * 0.5) / 14) * w;
        feather(x + (R() - 0.5) * 8, y, 52 + R() * 18, 9 + R() * 5, "#26362c", 0.55);
        feather(x + (R() - 0.5) * 8, y + 4, 30 + R() * 12, 4 + R() * 3, "#3c5244", 0.3);
      }
    }
    // gold barb catch-lights
    for (let i = 0; i < 46; i++) {
      g.fillStyle = "rgba(255,208,138,0.16)";
      g.beginPath(); g.arc(R() * w, R() * h, 1 + R() * 1.6, 0, 7); g.fill();
    }
  });
}

// The amber regard — vast round golden eyes, dark round pupils,
// unblinking. The card's serene menace.
function cdcEyeTex() {
  return canvasTex("cdc_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.35, "#ffb547");
    r.addColorStop(0.68, "#d98d1e");
    r.addColorStop(0.9, "#4a2c08");
    r.addColorStop(1, "#120c04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(12,9,5,0.94)";
    g.beginPath(); g.arc(64, 66, 17, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(48, 44, 6.5, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.4)";
    g.beginPath(); g.arc(78, 82, 3, 0, 7); g.fill();
  });
}

// The blade-ears — two vast scythes sweeping up and out, built
// as tapering bead-chains along the card's curve (deterministic).
const CDC_BLADE = (() => {
  const N = 6;
  const out = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    out.push([
      0.06 + Math.sin(f * 1.35) * 0.34,
      0.10 + f * 0.52,
      -0.04 - f * 0.10,
      0.085 * (1 - f * 0.72),
    ]);
  }
  return out;
})();

// Whisker-spines radiating from the jaw — angles fixed, per side.
const CDC_SPINES = [
  { y: 0.08, z: 0.10, ang: 0.55, len: 0.34 },
  { y: 0.02, z: 0.14, ang: 0.25, len: 0.40 },
  { y: -0.04, z: 0.14, ang: -0.05, len: 0.38 },
  { y: -0.09, z: 0.10, ang: -0.4, len: 0.30 },
];

export function ChaosDragonChampionBody({ pal }) {
  pal = pal || getBeingPalette("chaos_dragon");
  const scaleT = cdcScaleTex();
  const robeT = cdcRobeTex();
  const eye = cdcEyeTex();
  const flame = useRef();
  const flameCore = useRef();
  const discs = useRef([]);
  const wingL = useRef();
  const wingR = useRef();
  const embers = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the candle-flame gutters — never still, never out
    if (flame.current) {
      flame.current.scale.set(
        1 + Math.sin(t * 5.1) * 0.08,
        1 + Math.sin(t * 3.7 + 0.6) * 0.14,
        1 + Math.sin(t * 4.4 + 1.9) * 0.08
      );
      flame.current.rotation.z = Math.sin(t * 2.6) * 0.06;
    }
    if (flameCore.current) {
      flameCore.current.material.opacity = 0.85 + Math.sin(t * 6.2) * 0.12;
    }
    // the tall wings hold their fold, breathing slow
    if (wingL.current) wingL.current.rotation.z = 0.15 + Math.sin(t * 0.9) * 0.05;
    if (wingR.current) wingR.current.rotation.z = 0.15 + Math.sin(t * 0.9 + 0.5) * 0.05;
    // the twin suns bob slow, half a phase apart
    discs.current.forEach((m, i) => {
      if (!m) return;
      m.position.y = 0.62 + Math.sin(t * 0.8 + i * Math.PI) * 0.04;
      m.material.opacity = 0.3 + Math.sin(t * 1.1 + i * Math.PI) * 0.08;
    });
    embers.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.5 + i * 1.7;
      m.position.set(Math.cos(p) * 0.64, 0.66 + Math.sin(t * 1.3 + i) * 0.2, Math.sin(p) * 0.64 + 0.05);
      m.material.opacity = 0.3 + Math.sin(t * 1.9 + i) * 0.16;
    });
  });

  const hide = { map: scaleT, roughness: 0.88 };
  const robe = { map: robeT, roughness: 0.98 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe — dark feathers, shoulders to the ground ── */}
      <mesh position={[0, 0.46, 0]} scale={[1, 1, 0.88]} castShadow>
        <coneGeometry args={[0.36, 0.92, 14]} />
        <meshStandardMaterial {...robe} />
      </mesh>
      {/* shoulder mantle */}
      <mesh position={[0, 0.84, 0]} scale={[1.15, 0.5, 1.0]}>
        <sphereGeometry args={[0.21, 14, 10]} />
        <meshStandardMaterial {...robe} />
      </mesh>
      {/* feather fringe at the hem */}
      {Array.from({ length: 10 }, (_, i) => {
        const a = (i / 10) * Math.PI * 2;
        return (
          <mesh
            key={"hem" + i}
            position={[Math.cos(a) * 0.30, 0.09, Math.sin(a) * 0.27]}
            rotation={[Math.sin(a) * 0.35, 0, Math.cos(a) * -0.35]}
          >
            <coneGeometry args={[0.05, 0.2, 5]} />
            <meshStandardMaterial {...robe} />
          </mesh>
        );
      })}
      {/* collar ruff ringing the throat */}
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i / 8) * Math.PI * 2;
        return (
          <mesh
            key={"ruff" + i}
            position={[Math.cos(a) * 0.15, 0.92, Math.sin(a) * 0.13]}
            rotation={[0.4 + Math.sin(a) * 0.2, 0, Math.cos(a) * -0.8]}
          >
            <coneGeometry args={[0.045, 0.17, 5]} />
            <meshStandardMaterial color={CDC.robeHi} roughness={1} />
          </mesh>
        );
      })}

      {/* ── slim scaled arms from the robe, claws at rest ── */}
      {[-1, 1].map((sd) => (
        <group key={"arm" + sd}>
          <mesh position={[sd * 0.22, 0.62, 0.14]} rotation={[0.35, 0, sd * 0.3]} castShadow>
            <capsuleGeometry args={[0.042, 0.24, 4, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          <mesh position={[sd * 0.25, 0.46, 0.24]} scale={[0.9, 0.7, 1.2]}>
            <sphereGeometry args={[0.055, 10, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
        </group>
      ))}

      {/* ── clawed feet at the hem ── */}
      {[-0.13, 0.13].map((ox, i) => (
        <group key={"foot" + i} position={[ox, 0.055, 0.22]}>
          <mesh scale={[1, 0.6, 1.3]}>
            <sphereGeometry args={[0.08, 10, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          {[-0.035, 0, 0.035].map((tx, k) => (
            <mesh key={"claw" + k} position={[tx, -0.01, 0.1]} rotation={[1.35, 0, 0]}>
              <coneGeometry args={[0.016, 0.06, 5]} />
              <meshStandardMaterial color={CDC.whisker} roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the neck, short and ruffed ── */}
      <mesh position={[0, 0.96, 0.05]}>
        <sphereGeometry args={[0.11, 12, 10]} />
        <meshStandardMaterial {...hide} />
      </mesh>

      {/* ── the head — verdigris skull, humbled wedge snout ── */}
      <group position={[0, 1.06, 0.10]}>
        <mesh scale={[0.88, 0.85, 1.0]} castShadow>
          <sphereGeometry args={[0.21, 18, 14]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* small reptilian wedge + nostrils (round 2: snout humbled) */}
        <mesh position={[0, -0.04, 0.17]} scale={[0.62, 0.48, 0.95]}>
          <sphereGeometry args={[0.11, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"nos" + sd} position={[sd * 0.028, -0.02, 0.265]}>
            <sphereGeometry args={[0.011, 6, 6]} />
            <meshStandardMaterial color={CDC.nostril} roughness={1} />
          </mesh>
        ))}
        {/* the amber lamps in shadowed sockets */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.11, 0.045, 0.12]} scale={[1.05, 1.15, 0.6]}>
              <sphereGeometry args={[0.088, 10, 8]} />
              <meshStandardMaterial color={CDC.scaleDeep} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.108, 0.05, 0.202]} rotation={[0, sd * 0.3, 0]}>
              <circleGeometry args={[0.076, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ── the candle-flame crest — the card's burning heart ── */}
        <group position={[0, 0.24, 0.0]}>
          <mesh>
            <coneGeometry args={[0.05, 0.09, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          <group ref={flame} position={[0, 0.13, 0]}>
            <mesh>
              <coneGeometry args={[0.055, 0.26, 8]} />
              <meshBasicMaterial color={pal.glow} transparent opacity={0.55} toneMapped={false} depthWrite={false} />
            </mesh>
            <mesh ref={flameCore} position={[0, -0.03, 0]}>
              <coneGeometry args={[0.028, 0.15, 8]} />
              <meshBasicMaterial color={CDC.flameCore} transparent opacity={0.9} toneMapped={false} depthWrite={false} />
            </mesh>
          </group>
        </group>
        {/* ── the blade-ears — two vast gilded scythes ── */}
        {[-1, 1].map((sd) => (
          <group key={"blade" + sd}>
            {CDC_BLADE.map(([bx, by, bz, br], i, arr) => {
              const edge = i >= arr.length - 2;
              return (
                <mesh
                  key={"b" + i}
                  position={[sd * bx, by, bz]}
                  rotation={[0, 0, sd * -0.35]}
                  scale={[0.42, 1.25, 0.8]}
                  castShadow={i === 0}
                >
                  <sphereGeometry args={[br, 10, 8]} />
                  <meshStandardMaterial
                    color={edge ? CDC.bladeEdge : CDC.blade}
                    map={edge ? null : scaleT}
                    roughness={0.8}
                    emissive={edge ? pal.glow : "#000000"}
                    emissiveIntensity={edge ? 0.45 : 0}
                    toneMapped={!edge}
                  />
                </mesh>
              );
            })}
          </group>
        ))}
        {/* ── whisker-spines radiating from the jaw ── */}
        {[-1, 1].map((sd) =>
          CDC_SPINES.map((sp, i) => (
            <mesh
              key={"spine" + sd + i}
              position={[sd * 0.15, sp.y, sp.z]}
              rotation={[0, sd * -0.5, sd * sp.ang]}
            >
              <boxGeometry args={[sp.len, 0.007, 0.007]} />
              <meshBasicMaterial color={CDC.whisker} transparent opacity={0.6} />
            </mesh>
          ))
        )}
      </group>

      {/* ── the tall folded wings, rising behind the robe ── */}
      {[-1, 1].map((sd) => (
        <group key={"wing" + sd} position={[sd * 0.16, 0.80, -0.16]} scale={[sd * 0.95, 0.95, 0.95]} rotation={[0.1, sd * -0.25, 0.35]}>
          <group ref={sd < 0 ? wingL : wingR}>
            <CDWing boneMat={hide} membrane={CDC.membrane} mOpacity={0.6} glow={pal.glow} />
          </group>
        </group>
      ))}

      {/* ── the twin golden suns, riding low behind the shoulders ── */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"disc" + sd}
          ref={(el) => (discs.current[i] = el)}
          position={[sd * 0.36, 0.62, -0.24]}
          rotation={[0, sd * 0.3, 0]}
        >
          <circleGeometry args={[0.13, 24]} />
          <meshBasicMaterial color={CDC.disc} transparent opacity={0.32} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* ── the tail, peeking from the hem behind ── */}
      {[[0, 0.1, -0.34, 0.055], [0.05, 0.09, -0.44, 0.045], [0.12, 0.1, -0.52, 0.036], [0.19, 0.13, -0.57, 0.028]].map(([x, y, z, r], i, arr) => {
        const tip = i === arr.length - 1;
        return (
          <mesh key={"t" + i} position={[x, y, z]}>
            <sphereGeometry args={[r, 10, 8]} />
            <meshStandardMaterial
              {...hide}
              emissive={tip ? pal.glow : "#000000"}
              emissiveIntensity={tip ? 0.9 : 0}
              toneMapped={!tip}
            />
          </mesh>
        );
      })}

      {/* ── the warm veil: rim aura + four ember motes ── */}
      <mesh position={[0, 0.62, 0.0]} scale={[0.9, 0.95, 1.05]}>
        <sphereGeometry args={[0.62, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.08} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={"ember" + i} ref={(el) => (embers.current[i] = el)}>
          <sphereGeometry args={[0.025, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as the Beb) */}
      <pointLight position={[0, 1.0, 0.6]} color={pal.glow} intensity={0.8} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// CHAOS DRAGON LORD — the Ascent completed (Batch 3D-9, round 2).
// Built to the card (Terror TCG CDL-07): the being Xavizu the
// D'aeternuz named a Threat and allied with rather than fight.
// A bone-white colossus presiding on a long serpentine neck —
// two vast ivory horns sweeping up and out, the burning brow-eye
// ringed in crimson, small golden watch-eyes scattered across
// the skull (the card shows MANY — they stay), a radiating fan
// of pale tendrils, crimson drapes falling from the chest, and
// — round 2 — vast ivory wings, spread. The muzzle narrowed to
// a reptilian wedge, nostrils set. It does not stalk. It regards.
//
// Contract: presiding footprint inside the Beb/Champion disc;
// head at ~1.12 so the worn Lord crown (PlayerBody, y=1.5) lands
// framed between the horns. One pointLight.
// ────────────────────────────────────────────────────────────
const CDL = {
  bone: "#e6dfd0",      // ivory hide
  boneDeep: "#b3a88f",
  socket: "#171009",    // dark eye sockets
  drape: "#a81f14",     // the crimson falls
  tendril: "#ece6d8",   // the pale fan
  membrane: "#f2e8d0",  // wing sail, pale gold-white
  nostril: "#4a3a2c",
};

// Ivory hide — bone streaks and hairline cracks over warm white,
// a faint crimson bleed at the low stops. Cached, seeded, shared.
function cdlBoneTex() {
  return canvasTex("cdl_bone_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#efe9db");
    base.addColorStop(0.55, "#ddd4c1");
    base.addColorStop(1, "#bfb298");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(9973);
    for (let i = 0; i < 900; i++) {
      g.fillStyle = R() > 0.6 ? "rgba(120,104,80,0.06)" : R() > 0.3 ? "rgba(255,255,250,0.06)" : "rgba(168,31,20,0.03)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // long bone streaks — the sinew of the card's face
    const streak = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 18; i++) {
      const x = R() * w, y = R() * h;
      streak(x, y, 110 + R() * 90, 8 + R() * 6, (R() - 0.5) * 0.4, "#c9bda2", 0.4);
      streak(x, y, 60 + R() * 50, 4 + R() * 3, (R() - 0.5) * 0.4, "#f5f0e4", 0.3);
    }
    // hairline cracks
    g.strokeStyle = "rgba(90,74,52,0.25)"; g.lineWidth = 1;
    for (let i = 0; i < 26; i++) {
      let x = R() * w, y = R() * h;
      g.beginPath(); g.moveTo(x, y);
      for (let k = 0; k < 4; k++) { x += (R() - 0.5) * 30; y += R() * 22; g.lineTo(x, y); }
      g.stroke();
    }
  });
}

// The golden watch — round pupil, patient. Shared by the main
// pair and the small scattered eyes.
function cdlEyeTex() {
  return canvasTex("cdl_eye_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff8d8");
    r.addColorStop(0.35, "#ffd257");
    r.addColorStop(0.7, "#d98d1e");
    r.addColorStop(0.92, "#5a350a");
    r.addColorStop(1, "#120c04");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,10,6,0.9)";
    g.beginPath(); g.arc(64, 66, 12, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(48, 44, 6, 0, 7); g.fill();
  });
}

// The brow-eye — the crimson-ringed regard on the Lord's brow.
function cdlBrowEyeTex() {
  return canvasTex("cdl_brow_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 62, 3, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.28, "#ff9b2e");
    r.addColorStop(0.55, "#e84a3a");
    r.addColorStop(0.78, "#5c120a");
    r.addColorStop(0.9, "#e84a3a");
    r.addColorStop(1, "#1a0604");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(14,8,5,0.92)";
    g.beginPath(); g.ellipse(64, 65, 9, 18, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.8)";
    g.beginPath(); g.arc(52, 46, 4.5, 0, 7); g.fill();
  });
}

// The vast ivory horns — bead-chains along the card's up-and-out
// sweep, thick at the temple, tapering past the crown line.
const CDL_HORN = (() => {
  const N = 7;
  const out = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    out.push([
      0.14 + Math.sin(f * 1.5) * 0.42,
      0.12 + f * 0.5 + f * f * 0.1,
      -0.06 - f * 0.06,
      0.10 * (1 - f * 0.68),
    ]);
  }
  return out;
})();

// The tendril fan — the radiating pale crown behind the skull.
const CDL_FAN = (() => {
  const out = [];
  for (let i = 0; i < 11; i++) {
    const a = -1.9 + (3.8 * i) / 10;
    out.push({ a, len: 0.5 - Math.abs(a) * 0.08, r: 0.05, ring: 0 });
  }
  for (let i = 0; i < 7; i++) {
    const a = -1.4 + (2.8 * i) / 6;
    out.push({ a, len: 0.32 - Math.abs(a) * 0.05, r: 0.04, ring: 1 });
  }
  return out;
})();

// The small watch-eyes — scattered across the skull per the card.
// [x, y, z, radius] in head-space; the card shows many, so many stay.
const CDL_SMALL_EYES = [
  [-0.20, 0.14, 0.16, 0.036],
  [0.20, 0.14, 0.16, 0.036],
  [-0.10, -0.05, 0.235, 0.03],
  [0.10, -0.05, 0.235, 0.03],
];

export function ChaosDragonLordBody({ pal }) {
  pal = pal || getBeingPalette("chaos_dragon");
  const bone = cdlBoneTex();
  const eye = cdlEyeTex();
  const brow = cdlBrowEyeTex();
  const fan = useRef([]);
  const browRef = useRef();
  const drapes = useRef([]);
  const wingL = useRef();
  const wingR = useRef();
  const wisps = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the tendril fan breathes, each plume on its own fixed phase
    fan.current.forEach((grp, i) => {
      if (!grp) return;
      grp.rotation.z = -CDL_FAN[i].a + Math.sin(t * 0.65 + i * 0.85) * 0.035;
    });
    // the brow-eye's slow pulse
    if (browRef.current) {
      const p = 1 + Math.sin(t * 1.0) * 0.05;
      browRef.current.scale.set(p, p, 1);
    }
    // the great wings hold their spread, breathing like tide
    if (wingL.current) wingL.current.rotation.z = Math.sin(t * 0.55) * 0.05;
    if (wingR.current) wingR.current.rotation.z = Math.sin(t * 0.55 + 0.7) * 0.05;
    // the crimson drapes sway barely
    drapes.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.x = 0.12 + Math.sin(t * 0.7 + i * 2.4) * 0.04;
    });
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.42 + i * 1.35;
      m.position.set(Math.cos(p) * 0.82, 0.78 + Math.sin(t * 1.05 + i) * 0.24, Math.sin(p) * 0.82);
      m.material.opacity = 0.28 + Math.sin(t * 1.5 + i) * 0.14;
    });
  });

  const hide = { map: bone, roughness: 0.9 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the presiding mass: haunches + upright chest ── */}
      <mesh position={[0, 0.30, -0.08]} scale={[1.28, 0.72, 1.2]} castShadow>
        <sphereGeometry args={[0.40, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>
      <mesh position={[0, 0.58, 0.04]} scale={[1.0, 0.95, 1.02]} castShadow>
        <sphereGeometry args={[0.40, 18, 14]} />
        <meshStandardMaterial {...hide} />
      </mesh>

      {/* ── the crimson drapes falling from the chest ── */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"drape" + sd}
          ref={(el) => (drapes.current[i] = el)}
          position={[sd * 0.26, 0.52, 0.32]}
          rotation={[0.12, 0, sd * 0.1]}
        >
          <coneGeometry args={[0.09, 0.5, 6]} />
          <meshStandardMaterial color={CDL.drape} roughness={0.85} transparent opacity={0.9} emissive={CDL.drape} emissiveIntensity={0.12} />
        </mesh>
      ))}

      {/* ── the serpentine neck, rising ── */}
      {[[0, 0.74, 0.14, 0.17], [0, 0.88, 0.2, 0.155], [0, 1.0, 0.23, 0.145]].map(([x, y, z, r], i) => (
        <mesh key={"neck" + i} position={[x, y, z]}>
          <sphereGeometry args={[r, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
      ))}

      {/* ── the head — broad ivory skull, the many-eyed regard ── */}
      <group position={[0, 1.12, 0.26]}>
        <mesh scale={[1.05, 0.92, 0.95]} castShadow>
          <sphereGeometry args={[0.29, 18, 14]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* the heavy brow ridge */}
        <mesh position={[0, 0.12, 0.13]} scale={[1.15, 0.5, 0.75]}>
          <sphereGeometry args={[0.2, 14, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {/* reptilian wedge muzzle + nostrils (round 2: narrowed) */}
        <mesh position={[0, -0.08, 0.22]} scale={[0.78, 0.55, 1.1]}>
          <sphereGeometry args={[0.14, 12, 10]} />
          <meshStandardMaterial {...hide} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"nos" + sd} position={[sd * 0.045, -0.055, 0.365]}>
            <sphereGeometry args={[0.016, 6, 6]} />
            <meshStandardMaterial color={CDL.nostril} roughness={1} />
          </mesh>
        ))}
        {/* the golden pair, deep in shadowed sockets */}
        {[-1, 1].map((sd) => (
          <group key={"eye" + sd}>
            <mesh position={[sd * 0.14, 0.035, 0.17]} scale={[1, 1.05, 0.6]}>
              <sphereGeometry args={[0.095, 10, 8]} />
              <meshStandardMaterial color={CDL.socket} roughness={1} />
            </mesh>
            <mesh position={[sd * 0.135, 0.04, 0.262]} rotation={[0, sd * 0.24, 0]}>
              <circleGeometry args={[0.072, 20]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* the burning brow-eye, crimson-ringed */}
        <group position={[0, 0.17, 0.245]}>
          <mesh scale={[1, 1.1, 0.6]}>
            <sphereGeometry args={[0.062, 10, 8]} />
            <meshStandardMaterial color={CDL.socket} roughness={1} />
          </mesh>
          <mesh ref={browRef} position={[0, 0.004, 0.042]} rotation={[-0.2, 0, 0]}>
            <circleGeometry args={[0.05, 18]} />
            <meshBasicMaterial map={brow} toneMapped={false} />
          </mesh>
        </group>
        {/* the small watch-eyes, scattered — the card shows many */}
        {CDL_SMALL_EYES.map(([x, y, z, r], i) => (
          <group key={"small" + i} position={[x, y, z]}>
            <mesh scale={[1, 1.1, 0.55]}>
              <sphereGeometry args={[r * 1.35, 8, 6]} />
              <meshStandardMaterial color={CDL.socket} roughness={1} />
            </mesh>
            <mesh position={[0, 0, r * 0.85]} rotation={[0, x > 0 ? 0.5 : x < 0 ? -0.5 : 0, 0]}>
              <circleGeometry args={[r, 14]} />
              <meshBasicMaterial map={eye} toneMapped={false} />
            </mesh>
          </group>
        ))}
        {/* ── the vast ivory horns, up and out ── */}
        {[-1, 1].map((sd) => (
          <group key={"horn" + sd}>
            {CDL_HORN.map(([hx, hy, hz, hr], i) => (
              <mesh
                key={"h" + i}
                position={[sd * hx, hy, hz]}
                scale={[0.85, 1.1, 0.85]}
                castShadow={i === 0}
              >
                <sphereGeometry args={[hr, 12, 10]} />
                <meshStandardMaterial {...hide} />
              </mesh>
            ))}
          </group>
        ))}
        {/* ── the tendril fan — the pale radiating crown ── */}
        {CDL_FAN.map((p, i) => (
          <group
            key={"fan" + i}
            ref={(el) => (fan.current[i] = el)}
            position={[
              Math.sin(p.a) * (p.ring ? 0.22 : 0.28),
              Math.cos(p.a) * (p.ring ? 0.18 : 0.22) + 0.02,
              -0.15 - p.ring * 0.07,
            ]}
            rotation={[0.12, 0, -p.a]}
          >
            <mesh position={[0, p.len / 2 - 0.03, 0]}>
              <coneGeometry args={[p.r, p.len, 6]} />
              <meshStandardMaterial color={CDL.tendril} roughness={0.95} emissive={pal.glow} emissiveIntensity={p.ring ? 0.14 : 0.07} />
            </mesh>
          </group>
        ))}
      </group>

      {/* ── the vast ivory wings, spread ── */}
      {[-1, 1].map((sd) => (
        <group key={"wing" + sd} position={[sd * 0.24, 0.72, -0.22]} scale={[sd * 1.35, 1.35, 1.35]} rotation={[0.15, sd * -0.55, 0.1]}>
          <group ref={sd < 0 ? wingL : wingR}>
            <CDWing boneMat={hide} membrane={CDL.membrane} mOpacity={0.5} glow={pal.glow} />
          </group>
        </group>
      ))}

      {/* ── ivory forelegs, planted ── */}
      {[-1, 1].map((sd) => (
        <group key={"fore" + sd}>
          <mesh position={[sd * 0.22, 0.3, 0.34]} rotation={[0.28, 0, sd * 0.05]} castShadow>
            <capsuleGeometry args={[0.07, 0.24, 4, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
          <mesh position={[sd * 0.23, 0.07, 0.44]} scale={[1, 0.6, 1.25]}>
            <sphereGeometry args={[0.095, 10, 8]} />
            <meshStandardMaterial {...hide} />
          </mesh>
        </group>
      ))}

      {/* ── the tail-coil resting at the base ── */}
      {[[-0.32, 0.12, -0.4, 0.09], [-0.44, 0.09, -0.5, 0.075], [-0.52, 0.07, -0.58, 0.06], [-0.56, 0.06, -0.64, 0.045]].map(([x, y, z, r], i, arr) => {
        const tip = i === arr.length - 1;
        return (
          <mesh key={"coil" + i} position={[x, y, z]}>
            <sphereGeometry args={[r, 10, 8]} />
            <meshStandardMaterial
              {...hide}
              emissive={tip ? pal.glow : "#000000"}
              emissiveIntensity={tip ? 0.8 : 0}
              toneMapped={!tip}
            />
          </mesh>
        );
      })}

      {/* ── the veil, widened to a Threat's measure ── */}
      <mesh position={[0, 0.72, 0.02]} scale={[1.05, 0.95, 1.15]}>
        <sphereGeometry args={[0.85, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.09} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.03, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.34} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (light budget: one, same as every dragon) */}
      <pointLight position={[0, 0.8, 0.4]} color={pal.glow} intensity={0.7} distance={3.2} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// Z4Z DEMON — the gorgeous pass (Batch 3D-9). Beb tier.
// Built to the card (Terror TCG ZAZ-01): "they'll destroy
// anything for Chao." A black-iron colossus in a robe of plate —
// twin teal lens-eyes on a slab head, a flat cap with its
// antenna, two crimson crescent horns rising behind, two vast
// black claw-arms curving down like scythes, a burning red
// sigil on the chest (the flame-crown and its eye), and red
// flame-teeth licking up from the hem. A machine, so the slabs
// STAY slabs — boxes are honest geometry here; the card is made
// of them.
//
// Contract: head at the old z4z slab's height (~y 1.35), one
// pointLight at the old spec ([0, 1.4, 0.4], 0.7, 3). Colors
// are display-only dressing over the canon palette.
// ────────────────────────────────────────────────────────────
const Z4D = {
  iron: "#171a1e",      // black iron
  ironDeep: "#0b0d10",
  horn: "#c93a2c",      // the crimson crescents
  lens: "#3ec8d8",      // twin teal lenses
  sigil: "#e8402e",     // the chest flame-crown
  gear: "#d8b24a",      // the small gold watch-gear (canon main)
  flame: "#c93a2c",     // hem flame-teeth
};

// Black iron — riveted panel seams, scratches, a faint red rune
// bleed low in the plate. Cached, seeded, shared.
function z4dIronTex() {
  return canvasTex("z4d_iron_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#1e2228");
    base.addColorStop(0.55, "#15181d");
    base.addColorStop(1, "#0b0d10");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(3313);
    // panel seams
    g.strokeStyle = "rgba(0,0,0,0.45)"; g.lineWidth = 2;
    for (let i = 0; i < 7; i++) {
      const y = (i / 7) * h + R() * 24;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    for (let i = 0; i < 5; i++) {
      const x = (i / 5) * w + R() * 30;
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x, h); g.stroke();
    }
    // rivets along the seams
    for (let i = 0; i < 120; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(120,130,145,0.35)" : "rgba(0,0,0,0.5)";
      g.beginPath(); g.arc(R() * w, R() * h, 1.6 + R() * 1.6, 0, 7); g.fill();
    }
    // scratches + the red bleed
    g.strokeStyle = "rgba(150,160,175,0.12)"; g.lineWidth = 1;
    for (let i = 0; i < 40; i++) {
      const x = R() * w, y = R() * h;
      g.beginPath(); g.moveTo(x, y); g.lineTo(x + (R() - 0.5) * 60, y + (R() - 0.5) * 24); g.stroke();
    }
    for (let i = 0; i < 26; i++) {
      g.fillStyle = "rgba(200,58,44,0.05)";
      g.fillRect(R() * w, h * 0.55 + R() * h * 0.45, 3 + R() * 8, 3 + R() * 8);
    }
  });
}

// The twin lenses — cold teal optics, a dark aperture, one hard
// specular. Machine eyes: perfect circles, no soul offered.
function z4dLensTex() {
  return canvasTex("z4d_lens_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#d8fbff");
    r.addColorStop(0.3, "#3ec8d8");
    r.addColorStop(0.66, "#177785");
    r.addColorStop(0.9, "#062a30");
    r.addColorStop(1, "#02090b");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(2,10,12,0.9)";
    g.beginPath(); g.arc(64, 64, 14, 0, 7); g.fill();
    g.strokeStyle = "rgba(216,251,255,0.5)"; g.lineWidth = 3;
    g.beginPath(); g.arc(64, 64, 44, 0, 7); g.stroke();
    g.fillStyle = "rgba(255,255,255,0.9)";
    g.beginPath(); g.arc(48, 44, 6, 0, 7); g.fill();
  });
}

// The chest sigil — the flame-crown and its eye, painted on a
// transparent ground so it rides the robe as a living decal.
function z4dSigilTex() {
  return canvasTex("z4d_sigil_v1", 256, 256, (g) => {
    const stroke = (x, y, len, wd, ang, fill, alpha) => {
      g.save(); g.translate(x, y); g.rotate(ang);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    // the radiating flame-crown
    for (let i = 0; i < 9; i++) {
      const a = -1.6 + (3.2 * i) / 8;
      stroke(128 + Math.sin(a) * 52, 108 - Math.cos(a) * 40, 64 - Math.abs(a) * 12, 9, a, "#e8402e", 0.9);
      stroke(128 + Math.sin(a) * 52, 108 - Math.cos(a) * 40, 36 - Math.abs(a) * 7, 4.5, a, "#ff8a5c", 0.7);
    }
    // the eye orb at the crown's heart
    const r = g.createRadialGradient(128, 140, 3, 128, 140, 34);
    r.addColorStop(0, "#ffd8a0");
    r.addColorStop(0.4, "#ff7040");
    r.addColorStop(0.75, "#a01f10");
    r.addColorStop(1, "rgba(90,16,8,0)");
    g.fillStyle = r; g.beginPath(); g.arc(128, 140, 34, 0, 7); g.fill();
    g.fillStyle = "rgba(20,4,2,0.95)";
    g.beginPath(); g.arc(128, 140, 9, 0, 7); g.fill();
    // the fangs beneath
    for (let i = 0; i < 4; i++) {
      const x = 100 + i * 19;
      stroke(x, 190, 30, 5.5, Math.PI, "#e8402e", 0.8);
    }
  });
}

// The crimson crescents — bead-chains rising and curling inward
// behind the head, per the card. Deterministic curve.
const Z4D_HORN = (() => {
  const N = 5;
  const out = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    out.push([
      0.30 + Math.sin(f * Math.PI * 0.9) * 0.18,
      1.40 + f * 0.44,
      -0.04 - f * 0.05,
      0.05 * (1 - f * 0.62),
    ]);
  }
  return out;
})();

// The claw-arms — vast black scythes off the shoulders, curving
// out, down, and forward to the pincer tips.
const Z4D_CLAW = [
  [0.36, 0.96, 0.05, 0.095],
  [0.47, 0.81, 0.09, 0.085],
  [0.54, 0.63, 0.12, 0.078],
  [0.57, 0.45, 0.12, 0.068],
  [0.55, 0.29, 0.10, 0.058],
];

export function Z4ZBody({ pal }) {
  pal = pal || getBeingPalette("z4z");
  const iron = z4dIronTex();
  const lens = z4dLensTex();
  const sigilT = z4dSigilTex();
  const sigil = useRef();
  const clawL = useRef();
  const clawR = useRef();
  const orb = useRef();
  const embers = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the sigil burns — never steady
    if (sigil.current) sigil.current.material.opacity = 0.8 + Math.sin(t * 2.3) * 0.14;
    // the claws breathe open and shut, half a phase apart
    if (clawL.current) clawL.current.rotation.z = Math.sin(t * 0.9) * 0.045;
    if (clawR.current) clawR.current.rotation.z = -Math.sin(t * 0.9 + Math.PI) * 0.045;
    // the antenna orb bobs
    if (orb.current) orb.current.position.y = 1.78 + Math.sin(t * 1.7) * 0.02;
    embers.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.5 + i * 2.1;
      m.position.set(Math.cos(p) * 0.6, 0.5 + Math.sin(t * 1.3 + i) * 0.2, Math.sin(p) * 0.6);
      m.material.opacity = 0.3 + Math.sin(t * 1.9 + i) * 0.16;
    });
  });

  const plate = { map: iron, metalness: 0.35, roughness: 0.6 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe of plate, hem to shoulder ── */}
      <mesh position={[0, 0.55, 0]} castShadow>
        <coneGeometry args={[0.36, 1.05, 12]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      {/* flame-teeth licking up from the hem */}
      {Array.from({ length: 6 }, (_, i) => {
        const a = (i / 6) * Math.PI * 2 + 0.3;
        return (
          <mesh
            key={"hemflame" + i}
            position={[Math.cos(a) * 0.29, 0.14, Math.sin(a) * 0.29]}
            rotation={[Math.sin(a) * -0.15, 0, Math.cos(a) * 0.15]}
          >
            <coneGeometry args={[0.05, 0.18, 5]} />
            <meshStandardMaterial color={Z4D.flame} emissive={Z4D.flame} emissiveIntensity={0.35} roughness={0.7} />
          </mesh>
        );
      })}
      {/* the burning chest sigil — flame-crown and eye */}
      <mesh ref={sigil} position={[0, 0.78, 0.285]} rotation={[-0.16, 0, 0]}>
        <circleGeometry args={[0.155, 24]} />
        <meshBasicMaterial map={sigilT} transparent opacity={0.9} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── shoulders + the slab head ── */}
      {[-1, 1].map((sd) => (
        <mesh key={"sh" + sd} position={[sd * 0.30, 1.06, 0]} castShadow>
          <sphereGeometry args={[0.13, 12, 10]} />
          <meshStandardMaterial {...plate} />
        </mesh>
      ))}
      <mesh position={[0, 1.34, 0]} castShadow>
        <boxGeometry args={[0.44, 0.32, 0.34]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      {/* the twin teal lenses */}
      {[-1, 1].map((sd) => (
        <mesh key={"lens" + sd} position={[sd * 0.10, 1.35, 0.176]}>
          <circleGeometry args={[0.068, 20]} />
          <meshBasicMaterial map={lens} toneMapped={false} />
        </mesh>
      ))}
      {/* the small gold watch-gear at the head's corner */}
      <group position={[0.165, 1.44, 0.173]}>
        <mesh>
          <circleGeometry args={[0.032, 12]} />
          <meshBasicMaterial color={Z4D.gear} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, 0.002]}>
          <circleGeometry args={[0.013, 8]} />
          <meshBasicMaterial color={Z4D.ironDeep} />
        </mesh>
      </group>
      {/* the flat cap, its riser, and the antenna */}
      <mesh position={[0, 1.532, 0]}>
        <boxGeometry args={[0.52, 0.06, 0.40]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      <mesh position={[0, 1.585, 0]}>
        <boxGeometry args={[0.28, 0.05, 0.24]} />
        <meshStandardMaterial {...plate} />
      </mesh>
      <mesh position={[0, 1.68, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.15, 5]} />
        <meshStandardMaterial color={pal.accent} metalness={0.5} roughness={0.5} />
      </mesh>
      <mesh ref={orb} position={[0, 1.78, 0]}>
        <sphereGeometry args={[0.034, 8, 8]} />
        <meshBasicMaterial color={Z4D.sigil} toneMapped={false} />
      </mesh>

      {/* ── the crimson crescent horns, rising behind ── */}
      {[-1, 1].map((sd) => (
        <group key={"horn" + sd}>
          {Z4D_HORN.map(([hx, hy, hz, hr], i) => (
            <mesh key={"h" + i} position={[sd * hx, hy, hz]}>
              <sphereGeometry args={[hr, 10, 8]} />
              <meshStandardMaterial color={Z4D.horn} emissive={Z4D.horn} emissiveIntensity={0.28} roughness={0.6} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the vast claw-arms, out, down, and forward ── */}
      {[-1, 1].map((sd) => (
        <group key={"claw" + sd} ref={sd < 0 ? clawL : clawR}>
          {Z4D_CLAW.map(([cx, cy, cz, cr], i) => (
            <mesh key={"c" + i} position={[sd * cx, cy, cz]} castShadow={i === 0}>
              <sphereGeometry args={[cr, 10, 8]} />
              <meshStandardMaterial {...plate} />
            </mesh>
          ))}
          {/* the pincer tips */}
          {[[0.51, 0.09], [0.58, 0.14]].map(([px, pz], k) => (
            <mesh key={"tip" + k} position={[sd * px, 0.18, pz]} rotation={[0.2, 0, sd * (k === 0 ? 0.35 : -0.25)]}>
              <coneGeometry args={[0.032, 0.16, 6]} />
              <meshStandardMaterial color={Z4D.ironDeep} metalness={0.4} roughness={0.5} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── three red embers drifting the plate ── */}
      {[0, 1, 2].map((i) => (
        <mesh key={"ember" + i} ref={(el) => (embers.current[i] = el)}>
          <sphereGeometry args={[0.024, 6, 6]} />
          <meshBasicMaterial color={Z4D.sigil} transparent opacity={0.35} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 1.4, 0.4]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// Z4Z WIZARD — the Ascent worn as flesh (Batch 3D-9). Champion.
// Built to the card (Terror TCG ZAZ-04): "Time is a cog in this
// machine's design." A verdigris enamel machine in scholar's
// dress — the wide flat hat with its watching finial, twin
// amber lenses on a slab face, a dark cloak over the shoulders,
// a rune column falling down the robe front, one articulated
// hand holding its instrument, and the pale moon-halo behind
// the head that the whole lineage carries.
//
// Contract: head at the old slab height (~y 1.36), one
// pointLight at the old spec. Display colors over canon palette.
// ────────────────────────────────────────────────────────────
const Z4W = {
  enamel: "#2e6e72",    // verdigris enamel
  enamelDeep: "#123638",
  cloak: "#0f2c30",
  lens: "#ffb547",      // twin amber lenses
  runeGold: "#d8b24a",  // canon main
  runeRed: "#c93a2c",
  halo: "#e8e2c8",
};

// Verdigris enamel — paneled teal with wear at the edges and a
// faint gold pinstripe. Cached, seeded, shared.
function z4wEnamelTex() {
  return canvasTex("z4w_enamel_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#38838a");
    base.addColorStop(0.5, "#2a666b");
    base.addColorStop(1, "#164044");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(6069);
    // panel seams
    g.strokeStyle = "rgba(6,20,22,0.4)"; g.lineWidth = 2;
    for (let i = 0; i < 6; i++) {
      const y = (i / 6) * h + R() * 26;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    // enamel wear
    for (let i = 0; i < 700; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(220,245,240,0.05)" : "rgba(6,20,22,0.08)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // faint gold pinstripes
    g.strokeStyle = "rgba(216,178,74,0.16)"; g.lineWidth = 1.4;
    for (let i = 0; i < 4; i++) {
      const x = (i / 4) * w + 30 + R() * 40;
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x, h); g.stroke();
    }
  });
}

// The amber lenses — warm optics over a dark aperture. Kin to
// the Demon's but lit like a reading lamp, not a hunt.
function z4wLensTex() {
  return canvasTex("z4w_lens_v1", 128, 128, (g) => {
    const r = g.createRadialGradient(64, 60, 4, 64, 64, 62);
    r.addColorStop(0, "#fff2c8");
    r.addColorStop(0.32, "#ffb547");
    r.addColorStop(0.68, "#b06a12");
    r.addColorStop(0.9, "#3a2204");
    r.addColorStop(1, "#0c0702");
    g.fillStyle = r; g.beginPath(); g.arc(64, 64, 62, 0, 7); g.fill();
    g.fillStyle = "rgba(12,7,2,0.9)";
    g.beginPath(); g.arc(64, 64, 13, 0, 7); g.fill();
    g.strokeStyle = "rgba(255,242,200,0.45)"; g.lineWidth = 3;
    g.beginPath(); g.arc(64, 64, 42, 0, 7); g.stroke();
    g.fillStyle = "rgba(255,255,255,0.85)";
    g.beginPath(); g.arc(47, 44, 5.5, 0, 7); g.fill();
  });
}

export function Z4ZChampionBody({ pal }) {
  pal = pal || getBeingPalette("z4z");
  const enamel = z4wEnamelTex();
  const lens = z4wLensTex();
  const runes = useRef([]);
  const finial = useRef();
  const halo = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the rune column pulses, one after another down the robe
    runes.current.forEach((m, i) => {
      if (!m) return;
      m.material.emissiveIntensity = 0.25 + Math.sin(t * 1.4 - i * 1.1) * 0.2;
    });
    // the finial watches — a slow turn, a small bob
    if (finial.current) {
      finial.current.rotation.y = t * 0.4;
      finial.current.position.y = 1.80 + Math.sin(t * 1.1) * 0.015;
    }
    // the moon-halo breathes
    if (halo.current) halo.current.material.opacity = 0.11 + Math.sin(t * 0.7) * 0.03;
  });

  const shell = { map: enamel, metalness: 0.3, roughness: 0.5 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe, hem to collar ── */}
      <mesh position={[0, 0.58, 0]} castShadow>
        <coneGeometry args={[0.33, 1.1, 12]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      {/* the dark cloak over the shoulders */}
      <mesh position={[0, 1.02, -0.02]} castShadow>
        <coneGeometry args={[0.35, 0.52, 12]} />
        <meshStandardMaterial color={Z4W.cloak} roughness={0.95} />
      </mesh>
      {/* the rune column down the front */}
      {[[0.965, Z4W.runeGold], [0.80, Z4W.runeRed], [0.635, Z4W.runeGold]].map(([y, c], i) => (
        <mesh
          key={"rune" + i}
          ref={(el) => (runes.current[i] = el)}
          position={[0, y, 0.155 + (0.965 - y) * 0.3]}
          scale={[1, 1.5, 0.4]}
        >
          <sphereGeometry args={[0.042, 8, 8]} />
          <meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.25} roughness={0.5} />
        </mesh>
      ))}
      {/* the working hand, holding its instrument at the chest */}
      <mesh position={[0.17, 0.95, 0.13]} rotation={[0.4, 0, -0.9]}>
        <capsuleGeometry args={[0.035, 0.18, 4, 8]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0.09, 0.88, 0.22]}>
        <sphereGeometry args={[0.042, 8, 8]} />
        <meshStandardMaterial color={Z4W.enamelDeep} metalness={0.3} roughness={0.6} />
      </mesh>
      <mesh position={[0.07, 0.88, 0.24]} rotation={[0.5, 0, 1.15]}>
        <cylinderGeometry args={[0.008, 0.008, 0.24, 5]} />
        <meshStandardMaterial color={Z4W.runeGold} metalness={0.6} roughness={0.4} emissive={Z4W.runeGold} emissiveIntensity={0.2} />
      </mesh>

      {/* ── the slab head + the amber regard ── */}
      <mesh position={[0, 1.36, 0]} castShadow>
        <boxGeometry args={[0.36, 0.27, 0.27]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0, 1.355, 0.137]}>
        <boxGeometry args={[0.30, 0.20, 0.012]} />
        <meshStandardMaterial color={Z4W.enamelDeep} metalness={0.3} roughness={0.6} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"lens" + sd} position={[sd * 0.082, 1.36, 0.148]}>
          <circleGeometry args={[0.062, 20]} />
          <meshBasicMaterial map={lens} toneMapped={false} />
        </mesh>
      ))}

      {/* ── the wide flat hat + the watching finial ── */}
      <mesh position={[0, 1.545, 0]}>
        <boxGeometry args={[0.58, 0.045, 0.58]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0, 1.625, 0]}>
        <boxGeometry args={[0.24, 0.13, 0.24]} />
        <meshStandardMaterial {...shell} />
      </mesh>
      <mesh position={[0, 1.72, 0]}>
        <cylinderGeometry args={[0.014, 0.02, 0.1, 6]} />
        <meshStandardMaterial color={pal.accent} metalness={0.5} roughness={0.5} />
      </mesh>
      <group ref={finial} position={[0, 1.80, 0]}>
        <mesh>
          <sphereGeometry args={[0.038, 10, 8]} />
          <meshStandardMaterial color={Z4W.runeGold} metalness={0.5} roughness={0.4} emissive={Z4W.runeGold} emissiveIntensity={0.3} />
        </mesh>
        <mesh position={[0, 0, 0.032]}>
          <circleGeometry args={[0.014, 8]} />
          <meshBasicMaterial color={Z4W.enamelDeep} />
        </mesh>
      </group>

      {/* ── the moon-halo behind the head ── */}
      <mesh ref={halo} position={[0, 1.42, -0.30]}>
        <circleGeometry args={[0.48, 28]} />
        <meshBasicMaterial color={Z4W.halo} transparent opacity={0.12} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* under-glow (light budget: one, same as the Demon) */}
      <pointLight position={[0, 1.4, 0.4]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// Z4Z H'EHLL'S SWORD — the Ascent completed (Batch 3D-9). Lord.
// Built to the card (Terror TCG ZAZ-07): "No one has ever been
// able to have a conversation with one of these beings." An
// ivory-steel knight standing in judgment — the rounded helm
// with its barred visor, vast pale pauldrons, the golden
// halo-ring emblem on the chest with its dark core and red
// point, a dark faulds-skirt with white veils falling at the
// front, articulated arms at rest, and behind it the card's two
// vast golden arcs, sweeping up like the memory of wings.
//
// Contract: helm at ~y 1.5 (the Lord crown at y 1.5 seats onto
// the helm crest — the knight is crowned where he stands). One
// pointLight at the old spec.
// ────────────────────────────────────────────────────────────
const Z4S = {
  steel: "#d8dcd2",     // ivory steel
  steelDeep: "#8a9088",
  skirt: "#232a30",
  veil: "#eef2ea",
  emblem: "#d8b24a",    // the golden halo-ring (canon main)
  core: "#171a1e",
  point: "#c93a2c",
  arc: "#e8c86a",       // the vast golden arcs
};

// Ivory steel — brushed grain, faint panel seams, gold flecks.
// Cached, seeded, shared.
function z4sSteelTex() {
  return canvasTex("z4s_steel_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#e6eae0");
    base.addColorStop(0.5, "#d2d6cc");
    base.addColorStop(1, "#aab0a6");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(8887);
    // brushed grain
    g.strokeStyle = "rgba(120,128,118,0.10)"; g.lineWidth = 1;
    for (let i = 0; i < 90; i++) {
      const y = R() * h;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y + (R() - 0.5) * 8); g.stroke();
    }
    // panel seams
    g.strokeStyle = "rgba(60,66,58,0.28)"; g.lineWidth = 2;
    for (let i = 0; i < 5; i++) {
      const y = (i / 5) * h + R() * 30;
      g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke();
    }
    // gold flecks
    for (let i = 0; i < 46; i++) {
      g.fillStyle = "rgba(216,178,74,0.25)";
      g.beginPath(); g.arc(R() * w, R() * h, 1 + R() * 1.8, 0, 7); g.fill();
    }
  });
}

export function Z4ZLordBody({ pal }) {
  pal = pal || getBeingPalette("z4z");
  const steel = z4sSteelTex();
  const emblem = useRef();
  const arcL = useRef();
  const arcR = useRef();
  const veils = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the chest halo-ring pulses like a patient engine
    if (emblem.current) emblem.current.material.emissiveIntensity = 0.5 + Math.sin(t * 1.2) * 0.22;
    // the golden arcs shimmer and lean, tide-slow
    if (arcL.current) {
      arcL.current.material.opacity = 0.34 + Math.sin(t * 0.6) * 0.08;
      arcL.current.rotation.z = 0.5 + Math.sin(t * 0.4) * 0.02;
    }
    if (arcR.current) {
      arcR.current.material.opacity = 0.34 + Math.sin(t * 0.6 + 1.1) * 0.08;
      arcR.current.rotation.z = -0.5 - Math.sin(t * 0.4 + 0.8) * 0.02;
    }
    // the white veils sway barely
    veils.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.x = 0.08 + Math.sin(t * 0.75 + i * 2.2) * 0.035;
    });
  });

  const armor = { map: steel, metalness: 0.5, roughness: 0.38 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the faulds-skirt, dark, to the ground ── */}
      <mesh position={[0, 0.44, 0]} castShadow>
        <coneGeometry args={[0.34, 0.86, 12]} />
        <meshStandardMaterial color={Z4S.skirt} metalness={0.25} roughness={0.7} />
      </mesh>
      {/* the white veils falling at the front */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"veil" + sd}
          ref={(el) => (veils.current[i] = el)}
          position={[sd * 0.15, 0.62, 0.22]}
          rotation={[0.08, 0, sd * 0.06]}
          scale={[1, 1, 0.4]}
        >
          <coneGeometry args={[0.10, 0.56, 8]} />
          <meshStandardMaterial color={Z4S.veil} roughness={0.9} transparent opacity={0.85} />
        </mesh>
      ))}
      {/* the belt ornament */}
      <mesh position={[0, 0.86, 0.24]}>
        <sphereGeometry args={[0.042, 10, 8]} />
        <meshStandardMaterial color={Z4S.emblem} metalness={0.6} roughness={0.35} emissive={Z4S.emblem} emissiveIntensity={0.2} />
      </mesh>

      {/* ── the chest, the pauldrons, the halo-ring emblem ── */}
      <mesh position={[0, 1.06, 0]} castShadow>
        <boxGeometry args={[0.54, 0.42, 0.32]} />
        <meshStandardMaterial {...armor} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"pld" + sd} position={[sd * 0.37, 1.25, 0]} scale={[1.1, 0.8, 1]} castShadow>
          <sphereGeometry args={[0.16, 14, 12]} />
          <meshStandardMaterial {...armor} />
        </mesh>
      ))}
      <group position={[0, 1.07, 0.168]}>
        <mesh ref={emblem} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.10, 0.018, 8, 24]} />
          <meshStandardMaterial color={Z4S.emblem} metalness={0.6} roughness={0.3} emissive={Z4S.emblem} emissiveIntensity={0.5} toneMapped={false} />
        </mesh>
        <mesh>
          <sphereGeometry args={[0.052, 12, 10]} />
          <meshStandardMaterial color={Z4S.core} metalness={0.3} roughness={0.6} />
        </mesh>
        <mesh position={[0, -0.15, 0.01]}>
          <sphereGeometry args={[0.018, 8, 6]} />
          <meshBasicMaterial color={Z4S.point} toneMapped={false} />
        </mesh>
      </group>

      {/* ── articulated arms, at rest ── */}
      {[-1, 1].map((sd) => (
        <group key={"arm" + sd}>
          <mesh position={[sd * 0.37, 1.05, 0.02]} rotation={[0.06, 0, sd * 0.12]}>
            <capsuleGeometry args={[0.05, 0.22, 4, 8]} />
            <meshStandardMaterial {...armor} />
          </mesh>
          <mesh position={[sd * 0.40, 0.88, 0.03]}>
            <sphereGeometry args={[0.055, 10, 8]} />
            <meshStandardMaterial color={Z4S.steelDeep} metalness={0.5} roughness={0.4} />
          </mesh>
          <mesh position={[sd * 0.40, 0.72, 0.06]} rotation={[0.14, 0, 0]}>
            <capsuleGeometry args={[0.042, 0.18, 4, 8]} />
            <meshStandardMaterial {...armor} />
          </mesh>
          <mesh position={[sd * 0.40, 0.58, 0.09]}>
            <sphereGeometry args={[0.05, 10, 8]} />
            <meshStandardMaterial {...armor} />
          </mesh>
        </group>
      ))}

      {/* ── the helm and its barred visor ── */}
      <mesh position={[0, 1.50, 0]} scale={[0.95, 1.05, 0.9]} castShadow>
        <sphereGeometry args={[0.17, 16, 14]} />
        <meshStandardMaterial {...armor} />
      </mesh>
      <mesh position={[0, 1.48, 0.115]} scale={[0.78, 0.85, 0.45]}>
        <sphereGeometry args={[0.12, 12, 10]} />
        <meshStandardMaterial color={Z4S.core} metalness={0.3} roughness={0.7} />
      </mesh>
      {[-0.032, 0, 0.032].map((ox, i) => (
        <mesh key={"bar" + i} position={[ox, 1.48, 0.168]}>
          <boxGeometry args={[0.011, 0.13, 0.008]} />
          <meshStandardMaterial color={Z4S.steel} metalness={0.5} roughness={0.4} />
        </mesh>
      ))}

      {/* ── the vast golden arcs, the memory of wings ── */}
      <mesh ref={arcL} position={[-0.16, 1.05, -0.30]} rotation={[0.1, 0.2, 0.5]}>
        <torusGeometry args={[0.58, 0.024, 8, 26, Math.PI * 0.55]} />
        <meshBasicMaterial color={Z4S.arc} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh ref={arcR} position={[0.16, 1.05, -0.30]} rotation={[0.1, -0.2, -0.5]} scale={[-1, 1, 1]}>
        <torusGeometry args={[0.58, 0.024, 8, 26, Math.PI * 0.55]} />
        <meshBasicMaterial color={Z4S.arc} transparent opacity={0.36} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* under-glow (light budget: one, same as every Z4Z) */}
      <pointLight position={[0, 1.4, 0.4]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// XULAN WANDERER — the gorgeous pass (Batch 3D-9). Beb tier.
// Built to the card (Terror TCG XLS-01): "a race of assassins
// that drift through the Void, in search of revenge." A tall
// hooded figure in deep teal robes sown with red blossoms, a
// gold-veiled face in the hood's shadow, the branch-staff held
// across the body still budding flowers, and the pale moon-halo
// behind the head. It does not walk — the lineage floats on its
// ring, and the ring stays exactly where it has always been.
//
// Contract: hood at the old cone-body's height (~y 1.5), the
// floating ring untouched at y 0.28, one pointLight at the old
// spec ([0, 1.4, 0.3], 0.7, 3). Display colors over canon.
// ────────────────────────────────────────────────────────────
const XLW = {
  robe: "#1d4a52",      // deep teal cloth
  robeDeep: "#0d262c",
  blossom: "#c93a52",   // the sown flowers
  blossomHi: "#e88aa0",
  veil: "#d8b24a",      // the gold face-veil
  branch: "#3a2c22",    // the budding staff
  halo: "#e8e6dc",
};

// The sown robe — deep teal cloth grain, stitch runs, and the
// red blossom clusters scattered like the card's. Cached, seeded.
function xlwRobeTex() {
  return canvasTex("xlw_robe_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#265962");
    base.addColorStop(0.5, "#1b454d");
    base.addColorStop(1, "#0d262c");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(2251);
    // cloth grain
    for (let i = 0; i < 800; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(220,245,240,0.03)" : "rgba(4,14,17,0.08)";
      g.fillRect(R() * w, R() * h, 2 + R() * 3, 2 + R() * 3);
    }
    // stitch runs — long faint verticals
    g.strokeStyle = "rgba(180,210,205,0.08)"; g.lineWidth = 1;
    for (let i = 0; i < 14; i++) {
      const x = R() * w;
      g.beginPath(); g.moveTo(x, 0); g.lineTo(x + (R() - 0.5) * 30, h); g.stroke();
    }
    // the blossom clusters
    for (let i = 0; i < 22; i++) {
      const cx = R() * w, cy = R() * h;
      for (let b = 0; b < 4 + Math.floor(R() * 3); b++) {
        const a = R() * Math.PI * 2, rr = R() * 12;
        g.fillStyle = R() > 0.4 ? "rgba(201,58,82,0.75)" : "rgba(232,138,160,0.6)";
        g.beginPath();
        g.arc(cx + Math.cos(a) * rr, cy + Math.sin(a) * rr, 2.4 + R() * 2.6, 0, 7);
        g.fill();
      }
      g.fillStyle = "rgba(216,178,74,0.5)";
      g.beginPath(); g.arc(cx, cy, 1.8, 0, 7); g.fill();
    }
  });
}

export function XulanBody({ pal }) {
  pal = pal || getBeingPalette("xulan");
  const robe = xlwRobeTex();
  const ring = useRef();
  const staff = useRef();
  const halo = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    // the void-ring turns, patient
    if (ring.current) ring.current.rotation.z = t * 0.3;
    // the branch-staff sways with the drift
    if (staff.current) staff.current.rotation.z = 1.28 + Math.sin(t * 0.8) * 0.03;
    // the moon-halo breathes
    if (halo.current) halo.current.material.opacity = 0.13 + Math.sin(t * 0.65) * 0.03;
  });

  const cloth = { map: robe, roughness: 0.9, transparent: true, opacity: 0.95 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the robe, hem to hood ── */}
      <mesh position={[0, 0.9, 0]} castShadow>
        <coneGeometry args={[0.4, 1.3, 12]} />
        <meshStandardMaterial {...cloth} />
      </mesh>
      {/* shoulder drapes */}
      {[-1, 1].map((sd) => (
        <mesh key={"drape" + sd} position={[sd * 0.21, 1.3, 0.02]} scale={[1, 1.25, 0.9]}>
          <sphereGeometry args={[0.13, 12, 10]} />
          <meshStandardMaterial {...cloth} />
        </mesh>
      ))}
      {/* raised blossoms riding the cloth */}
      {[[-0.16, 1.18, 0.20], [0.12, 1.05, 0.26], [-0.06, 0.82, 0.31], [0.19, 0.68, 0.30], [-0.2, 0.55, 0.30], [0.05, 0.42, 0.37]].map(([x, y, z], i) => (
        <mesh key={"bl" + i} position={[x, y, z]}>
          <sphereGeometry args={[0.026, 8, 6]} />
          <meshStandardMaterial color={i % 2 ? XLW.blossom : XLW.blossomHi} roughness={0.7} emissive={XLW.blossom} emissiveIntensity={0.12} />
        </mesh>
      ))}

      {/* ── the hood and the gold-veiled face ── */}
      <mesh position={[0, 1.5, -0.02]}>
        <sphereGeometry args={[0.32, 12, 12, 0, Math.PI * 2, 0, Math.PI / 1.6]} />
        <meshStandardMaterial {...cloth} />
      </mesh>
      <mesh position={[0, 1.46, 0.10]}>
        <sphereGeometry args={[0.13, 12, 10]} />
        <meshStandardMaterial color={XLW.robeDeep} roughness={1} />
      </mesh>
      <mesh position={[0, 1.44, 0.17]} scale={[0.85, 1, 0.45]}>
        <sphereGeometry args={[0.115, 12, 10]} />
        <meshStandardMaterial color={XLW.veil} roughness={0.6} emissive={XLW.veil} emissiveIntensity={0.25} />
      </mesh>

      {/* ── the branch-staff, held across the drift, budding ── */}
      <group ref={staff} position={[0.04, 1.0, 0.30]} rotation={[0.15, 0, 1.28]}>
        <mesh>
          <cylinderGeometry args={[0.012, 0.016, 0.9, 6]} />
          <meshStandardMaterial color={XLW.branch} roughness={0.95} />
        </mesh>
        {[[-0.3, 0.02], [0.05, 0.025], [0.32, 0.02]].map(([oy, r], i) => (
          <mesh key={"bud" + i} position={[0.02, oy, 0.015]}>
            <sphereGeometry args={[r, 8, 6]} />
            <meshStandardMaterial color={XLW.blossom} roughness={0.7} emissive={XLW.blossom} emissiveIntensity={0.15} />
          </mesh>
        ))}
      </group>
      {/* the sleeve reaching to the staff */}
      <mesh position={[0.14, 1.06, 0.18]} rotation={[0.5, 0, -0.7]}>
        <capsuleGeometry args={[0.05, 0.2, 4, 8]} />
        <meshStandardMaterial {...cloth} />
      </mesh>

      {/* ── the moon-halo behind the hood ── */}
      <mesh ref={halo} position={[0, 1.5, -0.26]}>
        <circleGeometry args={[0.40, 28]} />
        <meshBasicMaterial color={XLW.halo} transparent opacity={0.14} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the void-ring — the drift itself, kept exactly ── */}
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.28, 0]}>
        <ringGeometry args={[0.4, 0.5, 20]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>

      {/* under-glow (kept from the old body — light budget unchanged) */}
      <pointLight position={[0, 1.4, 0.3]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// XULAN SURGEON — the Ascent worn as flesh (Batch 3D-9).
// Champion. Built to the card (Terror TCG XLS-04): "Guardians
// of their weakest territories... these beings often abandon
// revenge." The wanderer, stilled — a vast-brimmed hat over a
// face of plain void, wide flowing blue-teal robes with hanging
// sleeves, the golden Y-stole falling from the shoulders to the
// hem, a warm ember-glow rising inside the robe's base, and the
// sun-halo behind. It has nothing to avenge anymore.
//
// Contract: hood-height preserved (~y 1.5 face, hat above), the
// floating ring untouched at y 0.28, one pointLight at the old
// spec. Display colors over canon.
// ────────────────────────────────────────────────────────────
const XLS = {
  robe: "#2a5a6e",      // flowing blue-teal
  robeDeep: "#10303c",
  hat: "#1a3a48",
  stole: "#d8a83a",     // the golden Y
  emberGlow: "#ff9b4a", // the warm hem light
  voidFace: "#0a1218",
  halo: "#efe3c2",
};

// The flowing robe — long soft fold streaks over blue-teal, and
// the warm glow painted rising from the hem. Cached, seeded.
function xlsRobeTex() {
  return canvasTex("xls_robe_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#336a80");
    base.addColorStop(0.55, "#265468");
    base.addColorStop(0.82, "#2e5060");
    base.addColorStop(1, "#7a5a3c");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(1481);
    // cloth grain
    for (let i = 0; i < 700; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(230,245,250,0.03)" : "rgba(6,18,24,0.07)";
      g.fillRect(R() * w, R() * h, 2 + R() * 3, 2 + R() * 3);
    }
    // long fold streaks
    const streak = (x, y, len, wd, fill, alpha) => {
      g.save(); g.translate(x, y);
      g.beginPath();
      g.moveTo(0, -len / 2);
      g.quadraticCurveTo(wd, -len * 0.1, 0, len / 2);
      g.quadraticCurveTo(-wd, -len * 0.1, 0, -len / 2);
      g.closePath();
      g.globalAlpha = alpha; g.fillStyle = fill; g.fill();
      g.restore(); g.globalAlpha = 1;
    };
    for (let i = 0; i < 16; i++) {
      const x = R() * w, y = R() * h;
      streak(x, y, 140 + R() * 100, 9 + R() * 6, "#12333f", 0.4);
      streak(x + 4, y, 90 + R() * 60, 4 + R() * 3, "#4a8296", 0.25);
    }
    // the warm rise from the hem
    const glow = g.createLinearGradient(0, h * 0.7, 0, h);
    glow.addColorStop(0, "rgba(255,155,74,0)");
    glow.addColorStop(1, "rgba(255,155,74,0.35)");
    g.fillStyle = glow; g.fillRect(0, h * 0.7, w, h * 0.3);
  });
}

export function XulanChampionBody({ pal }) {
  pal = pal || getBeingPalette("xulan");
  const robe = xlsRobeTex();
  const ring = useRef();
  const sleeves = useRef([]);
  const stole = useRef([]);
  const halo = useRef();
  const hemGlow = useRef();

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) ring.current.rotation.z = t * 0.28;
    // the hanging sleeves sway with the drift
    sleeves.current.forEach((m, i) => {
      if (!m) return;
      m.rotation.z = (i === 0 ? 0.5 : -0.5) + Math.sin(t * 0.7 + i * 2.4) * 0.05;
    });
    // the golden Y shimmers
    stole.current.forEach((m, i) => {
      if (!m) return;
      m.material.emissiveIntensity = 0.3 + Math.sin(t * 1.1 + i * 0.9) * 0.15;
    });
    // the sun-halo and the hem-ember breathe together, offset
    if (halo.current) halo.current.material.opacity = 0.13 + Math.sin(t * 0.6) * 0.03;
    if (hemGlow.current) hemGlow.current.material.opacity = 0.2 + Math.sin(t * 0.9 + 1.2) * 0.06;
  });

  const cloth = { map: robe, roughness: 0.9, transparent: true, opacity: 0.96 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the wide robe, hem to collar ── */}
      <mesh position={[0, 0.9, 0]} castShadow>
        <coneGeometry args={[0.44, 1.3, 14]} />
        <meshStandardMaterial {...cloth} />
      </mesh>
      {/* the hanging sleeves, wide as the card's ── */}
      {[-1, 1].map((sd, i) => (
        <mesh
          key={"slv" + sd}
          ref={(el) => (sleeves.current[i] = el)}
          position={[sd * 0.30, 1.06, 0.06]}
          rotation={[0.08, 0, sd * 0.5]}
          scale={[0.72, 1, 0.5]}
        >
          <coneGeometry args={[0.16, 0.6, 10]} />
          <meshStandardMaterial {...cloth} />
        </mesh>
      ))}
      {/* the golden Y-stole — shoulders to meeting, meeting to hem */}
      {[[-1, 0.42], [1, -0.42]].map(([sd, rz], i) => (
        <mesh
          key={"yarm" + i}
          ref={(el) => (stole.current[i] = el)}
          position={[sd * 0.085, 1.22, 0.185]}
          rotation={[-0.12, 0, rz]}
        >
          <boxGeometry args={[0.036, 0.30, 0.012]} />
          <meshStandardMaterial color={XLS.stole} metalness={0.4} roughness={0.45} emissive={XLS.stole} emissiveIntensity={0.3} />
        </mesh>
      ))}
      <mesh
        ref={(el) => (stole.current[2] = el)}
        position={[0, 0.72, 0.275]}
        rotation={[-0.32, 0, 0]}
      >
        <boxGeometry args={[0.036, 0.72, 0.012]} />
        <meshStandardMaterial color={XLS.stole} metalness={0.4} roughness={0.45} emissive={XLS.stole} emissiveIntensity={0.3} />
      </mesh>
      {/* the ember-glow rising inside the hem */}
      <mesh ref={hemGlow} position={[0, 0.16, 0]}>
        <coneGeometry args={[0.30, 0.26, 12, 1, true]} />
        <meshBasicMaterial color={XLS.emberGlow} transparent opacity={0.2} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the void face beneath the hat ── */}
      <mesh position={[0, 1.5, 0.08]}>
        <sphereGeometry args={[0.12, 12, 10]} />
        <meshStandardMaterial color={XLS.voidFace} roughness={1} />
      </mesh>
      {/* the collar closing around it */}
      <mesh position={[0, 1.38, 0.02]} scale={[1, 0.6, 0.95]}>
        <sphereGeometry args={[0.17, 12, 10]} />
        <meshStandardMaterial {...cloth} />
      </mesh>

      {/* ── the vast hat ── */}
      <mesh position={[0, 1.65, 0]}>
        <cylinderGeometry args={[0.5, 0.5, 0.032, 22]} />
        <meshStandardMaterial color={XLS.hat} roughness={0.85} />
      </mesh>
      <mesh position={[0, 1.76, 0]}>
        <coneGeometry args={[0.15, 0.22, 12]} />
        <meshStandardMaterial color={XLS.hat} roughness={0.85} />
      </mesh>

      {/* ── the sun-halo behind ── */}
      <mesh ref={halo} position={[0, 1.55, -0.28]}>
        <circleGeometry args={[0.5, 28]} />
        <meshBasicMaterial color={XLS.halo} transparent opacity={0.13} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the void-ring, kept exactly ── */}
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.28, 0]}>
        <ringGeometry args={[0.4, 0.5, 20]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>

      {/* under-glow (light budget: one, same as the Wanderer) */}
      <pointLight position={[0, 1.4, 0.3]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// XULAN SHADOW — the Ascent completed (Batch 3D-9). Lord.
// Built to the card (Terror TCG XLS-07): "Hate reverts the
// Surgeons to what they once were." A mountain of black cloth
// under a vast pale moon — the teal skull watching from the
// hood's dark hair, the golden filigree-tree emblem at the
// chest, and where the cloak parts, the spectral ribcage of
// what it once was, with a small skull resting at the hem.
// The card shows the dead thing wearing its own ghost — the
// strangeness stays.
//
// Contract: skull at ~y 1.52 (the Lord crown at y 1.5 seats
// into the hood's crest), the floating ring untouched at
// y 0.28, one pointLight at the old spec. Display colors over
// canon.
// ────────────────────────────────────────────────────────────
// Rear ghost-curls — two bead-chains coiling off the base, the
// Shadow's wake. Hand-tuned, rearward.
const XLH_CURLS = [
  [[-0.34, 0.20, -0.42], [-0.44, 0.15, -0.52], [-0.52, 0.11, -0.60], [-0.57, 0.09, -0.66], [-0.60, 0.08, -0.70]],
  [[0.34, 0.20, -0.42], [0.44, 0.15, -0.52], [0.52, 0.11, -0.60], [0.57, 0.09, -0.66], [0.60, 0.08, -0.70]],
];

const XLH = {
  cloak: "#0b1418",     // the black mountain
  cloakHi: "#16262c",
  skull: "#9fc4be",     // the teal skull
  socket: "#050a0c",
  hair: "#0a1013",
  tree: "#d8a83a",      // the filigree tree
  ribs: "#cfe0da",      // the spectral ribcage
  halo: "#ece9de",
};

// The mountain cloak — near-black with teal sheen streaks
// running like weather down a slope. Cached, seeded.
function xlhCloakTex() {
  return canvasTex("xlh_cloak_v1", 512, 512, (g, w, h) => {
    const base = g.createLinearGradient(0, 0, 0, h);
    base.addColorStop(0, "#16262c");
    base.addColorStop(0.5, "#0d181d");
    base.addColorStop(1, "#050a0c");
    g.fillStyle = base; g.fillRect(0, 0, w, h);
    const R = mulberry32(7333);
    for (let i = 0; i < 700; i++) {
      g.fillStyle = R() > 0.5 ? "rgba(120,180,175,0.03)" : "rgba(0,0,0,0.1)";
      g.fillRect(R() * w, R() * h, 2 + R() * 4, 2 + R() * 4);
    }
    // sheen streaks down the slope
    g.strokeStyle = "rgba(110,170,165,0.07)"; g.lineWidth = 2;
    for (let i = 0; i < 22; i++) {
      const x = R() * w;
      g.beginPath(); g.moveTo(x, R() * h * 0.3);
      g.quadraticCurveTo(x + (R() - 0.5) * 40, h * 0.6, x + (R() - 0.5) * 60, h);
      g.stroke();
    }
  });
}

// The spectral ribcage — spine, rib arcs, and the small pelvis
// bloom, painted pale on a transparent ground: the ghost the
// cloak parts to show.
function xlhRibsTex() {
  return canvasTex("xlh_ribs_v1", 256, 256, (g) => {
    g.strokeStyle = "rgba(207,224,218,0.85)"; g.lineWidth = 4;
    // the spine
    g.beginPath(); g.moveTo(128, 20); g.lineTo(128, 210); g.stroke();
    // rib arcs, symmetric, widening then closing
    g.lineWidth = 3;
    for (let i = 0; i < 7; i++) {
      const y = 44 + i * 24;
      const span = 34 + Math.sin((i / 6) * Math.PI) * 46;
      for (const sd of [-1, 1]) {
        g.beginPath();
        g.moveTo(128, y);
        g.quadraticCurveTo(128 + sd * span, y + 8, 128 + sd * span * 0.72, y + 22);
        g.stroke();
      }
    }
    // faint inner glow lines
    g.strokeStyle = "rgba(207,224,218,0.3)"; g.lineWidth = 7;
    g.beginPath(); g.moveTo(128, 30); g.lineTo(128, 205); g.stroke();
    // the small pelvis bloom
    g.strokeStyle = "rgba(207,224,218,0.7)"; g.lineWidth = 3;
    g.beginPath(); g.arc(128, 222, 16, Math.PI * 1.1, Math.PI * 1.9); g.stroke();
  });
}

// The filigree tree — gold branching on a transparent ground,
// the emblem the card sets at the Shadow's heart.
function xlhTreeTex() {
  return canvasTex("xlh_tree_v1", 128, 128, (g) => {
    g.strokeStyle = "rgba(216,168,58,0.9)"; g.lineWidth = 3;
    const branch = (x, y, ang, len, depth) => {
      if (depth === 0 || len < 4) return;
      const nx = x + Math.cos(ang) * len;
      const ny = y - Math.sin(ang) * len;
      g.lineWidth = Math.max(1, depth);
      g.beginPath(); g.moveTo(x, y); g.lineTo(nx, ny); g.stroke();
      branch(nx, ny, ang + 0.55, len * 0.62, depth - 1);
      branch(nx, ny, ang - 0.55, len * 0.62, depth - 1);
    };
    branch(64, 118, Math.PI / 2, 34, 5);
    // the ring around it
    g.strokeStyle = "rgba(216,168,58,0.5)"; g.lineWidth = 2.5;
    g.beginPath(); g.arc(64, 64, 58, 0, 7); g.stroke();
  });
}

export function XulanLordBody({ pal }) {
  pal = pal || getBeingPalette("xulan");
  const cloak = xlhCloakTex();
  const ribsT = xlhRibsTex();
  const treeT = xlhTreeTex();
  const ring = useRef();
  const ribs = useRef();
  const halo = useRef();
  const hairL = useRef();
  const hairR = useRef();
  const curls = useRef([]);
  const wisps = useRef([]);

  useFrame((s) => {
    const t = s.clock.elapsedTime;
    if (ring.current) ring.current.rotation.z = t * 0.24;
    // the wake-curls breathe, ghost-faint
    curls.current.forEach((m, i) => {
      if (!m) return;
      m.material.opacity = 0.26 + Math.sin(t * 1.1 + i * 0.7) * 0.1;
    });
    // the ghost within shimmers
    if (ribs.current) ribs.current.material.opacity = 0.55 + Math.sin(t * 0.9) * 0.14;
    // the vast moon breathes
    if (halo.current) halo.current.material.opacity = 0.15 + Math.sin(t * 0.5) * 0.035;
    // the hood's hair sways barely
    if (hairL.current) hairL.current.rotation.z = 0.12 + Math.sin(t * 0.6) * 0.03;
    if (hairR.current) hairR.current.rotation.z = -0.12 - Math.sin(t * 0.6 + 1.9) * 0.03;
    wisps.current.forEach((m, i) => {
      if (!m) return;
      const p = t * 0.4 + i * 1.3;
      m.position.set(Math.cos(p) * 0.8, 0.8 + Math.sin(t * 1.0 + i) * 0.24, Math.sin(p) * 0.8);
      m.material.opacity = 0.26 + Math.sin(t * 1.4 + i) * 0.13;
    });
  });

  const shroud = { map: cloak, roughness: 0.95 };

  return (
    <group scale={0.92}>{/* lowered a notch so the Codex frames the head (caP'n, r3) */}
      {/* ── the mountain: cloak + base flare ── */}
      <mesh position={[0, 0.85, 0]} castShadow>
        <coneGeometry args={[0.55, 1.3, 16]} />
        <meshStandardMaterial {...shroud} />
      </mesh>
      <mesh position={[0, 0.2, 0]} castShadow>
        <coneGeometry args={[0.62, 0.38, 16]} />
        <meshStandardMaterial {...shroud} />
      </mesh>

      {/* ── where the cloak parts: the spectral ribcage ── */}
      <mesh ref={ribs} position={[0, 0.60, 0.375]} rotation={[-0.4, 0, 0]}>
        <planeGeometry args={[0.36, 0.72]} />
        <meshBasicMaterial map={ribsT} transparent opacity={0.6} toneMapped={false} depthWrite={false} />
      </mesh>
      {/* the small skull resting at the hem */}
      <group position={[0, 0.15, 0.55]}>
        <mesh scale={[0.95, 1, 0.85]}>
          <sphereGeometry args={[0.05, 10, 8]} />
          <meshStandardMaterial color={XLH.ribs} roughness={0.9} />
        </mesh>
        {[-1, 1].map((sd) => (
          <mesh key={"bs" + sd} position={[sd * 0.018, 0.005, 0.038]}>
            <sphereGeometry args={[0.012, 6, 6]} />
            <meshStandardMaterial color={XLH.socket} roughness={1} />
          </mesh>
        ))}
      </group>

      {/* ── the filigree tree at the heart ── */}
      <mesh position={[0, 1.10, 0.185]} rotation={[-0.4, 0, 0]}>
        <circleGeometry args={[0.095, 22]} />
        <meshBasicMaterial map={treeT} transparent toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the hood, its hair, and the watching skull ── */}
      <mesh position={[0, 1.56, -0.03]}>
        <sphereGeometry args={[0.20, 14, 12, 0, Math.PI * 2, 0, Math.PI / 1.5]} />
        <meshStandardMaterial {...shroud} />
      </mesh>
      <group ref={hairL} position={[-0.14, 1.46, 0.05]}>
        <mesh rotation={[0, 0, 0.12]}>
          <coneGeometry args={[0.05, 0.34, 6]} />
          <meshStandardMaterial color={XLH.hair} roughness={1} />
        </mesh>
      </group>
      <group ref={hairR} position={[0.14, 1.46, 0.05]}>
        <mesh rotation={[0, 0, -0.12]}>
          <coneGeometry args={[0.05, 0.34, 6]} />
          <meshStandardMaterial color={XLH.hair} roughness={1} />
        </mesh>
      </group>
      <mesh position={[0, 1.52, 0.06]} scale={[0.9, 1.05, 0.85]}>
        <sphereGeometry args={[0.14, 14, 12]} />
        <meshStandardMaterial color={XLH.skull} roughness={0.85} />
      </mesh>
      {[-1, 1].map((sd) => (
        <mesh key={"socket" + sd} position={[sd * 0.05, 1.54, 0.165]} scale={[1, 1.15, 0.5]}>
          <sphereGeometry args={[0.038, 8, 6]} />
          <meshStandardMaterial color={XLH.socket} roughness={1} />
        </mesh>
      ))}
      <mesh position={[0, 1.475, 0.175]} scale={[0.8, 1.2, 0.5]}>
        <sphereGeometry args={[0.014, 6, 6]} />
        <meshStandardMaterial color={XLH.socket} roughness={1} />
      </mesh>

      {/* ── the cloak's back — fold ridges and the hood veil ── */}
      {[[-0.24, -0.30], [0, -0.38], [0.24, -0.30]].map(([x, z], i) => (
        <mesh key={"ridge" + i} position={[x, 0.78, z]} rotation={[0.32, 0, x * -0.35]} scale={[0.55, 1, 0.5]}>
          <coneGeometry args={[0.14, 1.0, 8]} />
          <meshStandardMaterial color={XLH.cloakHi} roughness={0.95} />
        </mesh>
      ))}
      <mesh position={[0, 1.28, -0.20]} rotation={[0.42, 0, 0]} scale={[1, 1, 0.4]}>
        <coneGeometry args={[0.17, 0.62, 10]} />
        <meshStandardMaterial map={xlhCloakTex()} roughness={0.95} transparent opacity={0.92} />
      </mesh>
      {/* ── two ghost-curls coiling off the base, rearward ── */}
      {XLH_CURLS.map((path, ci) => (
        <group key={"curl" + ci}>
          {path.map(([x, y, z], bi) => (
            <mesh key={"cb" + bi} ref={(el) => (curls.current[ci * 5 + bi] = el)} position={[x, y, z]}>
              <sphereGeometry args={[Math.max(0.018, 0.048 - bi * 0.006), 8, 6]} />
              <meshBasicMaterial color={XLH.ribs} transparent opacity={0.3} toneMapped={false} depthWrite={false} />
            </mesh>
          ))}
        </group>
      ))}

      {/* ── the vast moon behind ── */}
      <mesh ref={halo} position={[0, 1.52, -0.30]}>
        <circleGeometry args={[0.58, 30]} />
        <meshBasicMaterial color={XLH.halo} transparent opacity={0.15} side={THREE.DoubleSide} toneMapped={false} depthWrite={false} />
      </mesh>

      {/* ── the spectral veil: rim aura + five wisps ── */}
      <mesh position={[0, 0.85, 0]} scale={[1.0, 1.0, 1.0]}>
        <sphereGeometry args={[0.82, 16, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.07} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={"wisp" + i} ref={(el) => (wisps.current[i] = el)}>
          <sphereGeometry args={[0.028, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.32} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}

      {/* ── the void-ring, kept exactly ── */}
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.28, 0]}>
        <ringGeometry args={[0.4, 0.5, 20]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.5} side={THREE.DoubleSide} toneMapped={false} />
      </mesh>

      {/* under-glow (light budget: one, same as every Xulan) */}
      <pointLight position={[0, 1.4, 0.3]} color={pal.glow} intensity={0.7} distance={3} />
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// THE FOUR BEINGS — primitives in canon palettes. On-canon per
// BeingSprite.js: Tiger ghost-teal prowler, Dragon crimson hulk,
// Z4Z clockwork knight, Xulan floating hooded vessel.
// ────────────────────────────────────────────────────────────
export function BeingMesh({ beingId, pal, tier = TIERS.BEB }) {
  pal = pal || getBeingPalette(beingId);
  if (beingId === "chaos_dragon") {
    // The Ascent worn as flesh (3D-9). Tier picks the body; callers
    // that don't pass tier get the Beb, exactly like the tiger.
    if (tier === TIERS.LORD) return <ChaosDragonLordBody pal={pal} />;
    if (tier === TIERS.CHAMPION) return <ChaosDragonChampionBody pal={pal} />;
    return <ChaosDragonBody pal={pal} />;
  }
  if (beingId === "z4z") {
    // The Ascent worn as flesh (3D-9). Tier picks the body; callers
    // that don't pass tier get the Demon (Beb), like every lineage.
    if (tier === TIERS.LORD) return <Z4ZLordBody pal={pal} />;
    if (tier === TIERS.CHAMPION) return <Z4ZChampionBody pal={pal} />;
    return <Z4ZBody pal={pal} />;
  }
  if (beingId === "xulan") {
    // The Ascent worn as flesh (3D-9). Tier picks the body; callers
    // that don't pass tier get the Wanderer (Beb), like every lineage.
    if (tier === TIERS.LORD) return <XulanLordBody pal={pal} />;
    if (tier === TIERS.CHAMPION) return <XulanChampionBody pal={pal} />;
    return <XulanBody pal={pal} />;
  }
  // spectral_tiger (default) — the Ascent worn as flesh (3D-7/3D-8).
  // Tier picks the body; callers that don't pass tier get the Beb,
  // so every existing call site renders exactly what it did before.
  if (tier === TIERS.LORD) return <SpectralTigerLordBody pal={pal} />;
  if (tier === TIERS.CHAMPION) return <SpectralTigerChampionBody pal={pal} />;
  return <SpectralTigerBody pal={pal} />;
}

// Pre-Transcendence traveler — a small human, per the reversal lore.
export function TravelerMesh({ tone = "#c9a84c" }) {
  return (
    <group>
      <mesh position={[0, 0.55, 0]} castShadow><capsuleGeometry args={[0.22, 0.5, 4, 8]} /><meshStandardMaterial color={tone} roughness={0.9} /></mesh>
      <mesh position={[0, 1.1, 0]} castShadow><sphereGeometry args={[0.22, 12, 12]} /><meshStandardMaterial color="#e8d8b8" /></mesh>
      <mesh position={[0.28, 0.6, 0.1]} rotation={[0, 0, 0.3]}><boxGeometry args={[0.02, 0.28, 0.2]} /><meshStandardMaterial color="#f0d060" emissive="#f0d060" emissiveIntensity={0.6} toneMapped={false} /></mesh>
    </group>
  );
}

// ── The player body: being (or traveler) + the Ascent worn.
// Tier is passed in (derived from xp upstream, never stored).
// facing {x,y} yaws the body; moving toggles walk-bob vs breathe.
// swing = ctrl.lastSwing ({ id, step, at }) — a new id fires the
// per-being strike posture. dodging bends the body into a roll.
// On-canon per BeingSprite's STRIKE_ANIMS + beings.js lytAttack:
//   tiger  spectral_claws  2 hits  the pounce (lunge; swing 2 phases)
//   dragon dragons_bite    1 hit   rear + snap (heavy)
//   z4z    blade_cycle     3 hits  blade pivot (fast; scarab flash)
//   xulan  psychic_lance   1 hit   psychic surge (the lance TRAVELS)
// FX-1: per-ability cast-posture durations (seconds). Dials.
const CAST_POSE_S = {
  spectral_paws: 0.55,
  spectral_claws: 0.7,
  haunting_presence: 1.1,
  ghost_form: 0.6,
  nine_lives: 0.9,
};

export function PlayerBody({ beingId, tier, facing = { x: 0, y: 1 }, moving = false, dim = false, swing = null, dodging = false, cast = null, ghost = false, grace = false, pawsArmed = false }) {
  const pal = getBeingPalette(beingId);
  const bob = useRef();
  const strike = useRef();          // the strike-posture wrapper
  const flesh = useRef();           // FX-1: the body only (ghost traverse)
  const anim = useRef({ id: null, t: 1 });   // 0..1 progress of current swing
  const castAnim = useRef({ id: null, t: 1, ability: null }); // FX-1 cast pose
  const dodgeT = useRef(0);

  // a new swing id resets the strike clock to 0 (re-triggers)
  if (swing && swing.id !== anim.current.id) { anim.current.id = swing.id; anim.current.t = 0; }
  // FX-1: a new cast id starts its posture the same way
  if (cast && cast.id !== castAnim.current.id) {
    castAnim.current = { id: cast.id, t: 0, ability: cast.abilityId };
  }

  // FX-1: GHOST FORM — the body itself goes incorporeal. Traverse
  // the flesh group and thin every lit material; glow materials
  // (toneMapped === false — the eyes, stripe cores, emissive tips)
  // stay full-bright, burning through the veil. Originals stored on
  // userData and restored exactly on exit.
  useEffect(() => {
    const g = flesh.current;
    if (!g) return;
    g.traverse((c) => {
      if (!c.isMesh || !c.material) return;
      if (c.material.toneMapped === false) return; // the lamps stay lit
      if (ghost) {
        if (c.userData._preGhost === undefined) {
          c.userData._preGhost = { transparent: c.material.transparent, opacity: c.material.opacity };
        }
        c.material.transparent = true;
        c.material.opacity = 0.34;
      } else if (c.userData._preGhost !== undefined) {
        c.material.transparent = c.userData._preGhost.transparent;
        c.material.opacity = c.userData._preGhost.opacity;
        delete c.userData._preGhost;
      }
    });
  }, [ghost, beingId, tier]);

  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;
    if (bob.current) bob.current.position.y = moving ? 0.05 + Math.abs(Math.sin(t * 10)) * 0.12 : 0.02 + Math.sin(t * 2.5) * 0.04;

    // advance the strike (each swing ~0.28s)
    const a = anim.current;
    if (a.t < 1) a.t = Math.min(1, a.t + dt / 0.28);
    // FX-1: advance the cast posture on its own per-ability clock
    const c = castAnim.current;
    if (c.t < 1) c.t = Math.min(1, c.t + dt / (CAST_POSE_S[c.ability] || 0.6));
    // dodge lerps a roll in/out
    dodgeT.current += ((dodging ? 1 : 0) - dodgeT.current) * Math.min(1, dt * 10);

    if (strike.current) {
      const p = a.t;                       // 0..1
      const punch = p < 1 ? Math.sin(p * Math.PI) : 0;  // 0→1→0 arc
      const step = swing?.step || 1;
      if (beingId === "chaos_dragon") {
        // rear back then snap forward — weighty
        strike.current.rotation.x = -punch * 0.5 + (p < 0.4 ? 0 : Math.sin(p * Math.PI) * 0.3);
        strike.current.position.z = punch * 0.25;
      } else if (beingId === "z4z") {
        // fast alternating blade twist per combo step
        const dir = step % 2 === 0 ? -1 : 1;
        strike.current.rotation.y = dir * punch * 0.5;
        strike.current.position.z = punch * 0.18;
      } else if (beingId === "xulan") {
        // brace + surge (the lance itself travels — see StrikeFX)
        strike.current.position.y = punch * 0.12;
        strike.current.scale.setScalar(1 + punch * 0.06);
      } else {
        // tiger — THE POUNCE (FX-1). A real hunt beat: a quarter-
        // second crouch coils the body DOWN, then it springs up-
        // and-forward with a slight nose-down pitch. Swing 2 is
        // the phase swipe (userData.phase feeds the veil flash).
        const coil = p < 0.25 ? Math.sin((p / 0.25) * Math.PI) : 0;
        const spring = p >= 0.25 && p < 1 ? Math.sin(((p - 0.25) / 0.75) * Math.PI) : 0;
        strike.current.position.y = -coil * 0.1 + spring * 0.3;
        strike.current.position.z = spring * 0.55;
        strike.current.rotation.x = -spring * 0.18;
        const phaseSwing = step === 2;
        strike.current.scale.setScalar(1 + spring * 0.05);
        if (strike.current.userData) strike.current.userData.phase = phaseSwing ? spring : 0;
      }
      // FX-1: cast postures override the swing pose while playing
      if (c.t < 1 && c.ability) {
        const cp = c.t;
        const cPunch = Math.sin(cp * Math.PI);
        if (c.ability === "spectral_paws") {
          // the coil — head down, haunches up, paws igniting
          strike.current.position.y = -0.12 * cPunch;
          strike.current.rotation.x = 0.2 * cPunch;
        } else if (c.ability === "spectral_claws") {
          // rear back, then the launch that births the Shadow
          const rear = Math.min(1, cp / 0.4);
          strike.current.rotation.x = -0.35 * Math.sin(rear * Math.PI);
          strike.current.position.z = cp > 0.4 ? Math.sin(((cp - 0.4) / 0.6) * Math.PI) * 0.45 : 0;
        } else if (c.ability === "haunting_presence") {
          // HER move — the Tiger rears high and the world goes dark
          strike.current.rotation.x = -0.55 * cPunch;
          strike.current.position.y = 0.22 * cPunch;
        } else if (c.ability === "ghost_form") {
          // the rise between worlds
          strike.current.position.y = 0.2 * cPunch;
        } else if (c.ability === "nine_lives") {
          // dissolve at the killing blow — reform at the shadow
          strike.current.scale.setScalar(0.3 + 0.7 * Math.abs(1 - 2 * cp));
        }
      }
      // dodge roll — tuck + spin regardless of being
      strike.current.rotation.z = dodgeT.current * 0.6;
    }
  });

  const lord = tier === TIERS.LORD;
  const champ = tier === TIERS.CHAMPION || lord;
  const yaw = Math.atan2(facing.x || 0, facing.y || 1);
  const striking = swing && anim.current.t < 1;

  return (
    <group rotation={[0, yaw, 0]}>
      <group ref={bob}>
        <group ref={strike}>
          <group ref={flesh}>
            {beingId ? <BeingMesh beingId={beingId} pal={pal} tier={tier} /> : <TravelerMesh />}
          </group>
          {/* per-being strike FX — rakes / jaws / arc / lance */}
          {striking && <StrikeFX beingId={beingId} pal={pal} step={swing?.step || 1} />}
          {/* FX-1 worn layers — the hunt made visible */}
          {pawsArmed && <PawsFire pal={pal} />}
          {ghost && <GhostVeil pal={pal} />}
        </group>
        {grace && <GraceAura pal={pal} />}
        {champ && (
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.06, 0]}>
            <ringGeometry args={[0.5, 0.66, 24]} />
            <meshBasicMaterial color={pal.glow} transparent opacity={lord ? 0.8 : 0.45} side={THREE.DoubleSide} toneMapped={false} />
          </mesh>
        )}
        {lord && (
          <group position={[0, 1.5, 0]}>
            {[-0.18, 0, 0.18].map((ox, i) => (
              <mesh key={i} position={[ox, i === 1 ? 0.12 : 0, 0]}>
                <coneGeometry args={[0.06, i === 1 ? 0.28 : 0.2, 4]} />
                <meshStandardMaterial color={pal.glow} emissive={pal.glow} emissiveIntensity={0.8} toneMapped={false} />
              </mesh>
            ))}
          </group>
        )}
      </group>
      <GroundBlob opacity={dim ? 0.18 : 0.28} />
    </group>
  );
}

// ── STRIKE FX — the projected part of each swing (rakes/jaws/arc/
// lance). Lives here so both fields and dungeon get it. Keyed by
// being; the lance TRAVELS (Xulan is the only ranged being).
function StrikeFX({ beingId, pal, step }) {
  const ref = useRef();
  const t = useRef(0);
  // The tiger's claw rakes are a GROUP of meshes (no .material on
  // the root) — dragon/z4z/xulan are single meshes. One setter that
  // works for both shapes, so no branch can crash on a group.
  const setOpacity = (o) => {
    const r = ref.current;
    if (!r) return;
    if (r.material) r.material.opacity = o;
    else r.traverse((c) => { if (c.material) c.material.opacity = o; });
  };
  useFrame((_, dt) => {
    t.current = Math.min(1, t.current + dt / 0.26);
    if (!ref.current) return;
    const p = t.current;
    if (beingId === "xulan") {
      // psychic lance flies forward
      ref.current.position.z = 0.6 + p * 2.6;
      setOpacity(1 - p);
    } else {
      const punch = Math.sin(p * Math.PI);
      ref.current.scale.setScalar(0.6 + punch * 0.9);
      setOpacity(punch);
    }
  });

  if (beingId === "chaos_dragon") {
    // jaw-snap flare in front of the head
    return (
      <mesh ref={ref} position={[0, 0.95, 0.9]}>
        <coneGeometry args={[0.4, 0.9, 8]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.9} toneMapped={false} />
      </mesh>
    );
  }
  if (beingId === "z4z") {
    // blade arc — a thin bright crescent, side depends on combo step
    const dir = step % 2 === 0 ? -1 : 1;
    return (
      <mesh ref={ref} position={[0.3 * dir, 0.9, 0.5]} rotation={[0, 0, dir * 0.6]}>
        <torusGeometry args={[0.5, 0.05, 6, 12, Math.PI]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.9} toneMapped={false} />
      </mesh>
    );
  }
  if (beingId === "xulan") {
    // the traveling psychic lance
    return (
      <mesh ref={ref} position={[0, 1.1, 0.6]} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.12, 0.8, 6]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={1} toneMapped={false} />
      </mesh>
    );
  }
  // tiger — THE POUNCE RAKES (FX-1). Three curved phantom claw
  // arcs fanned across the strike, riding the lunge. Swing 2 (the
  // phase swipe) ghosts them wider and paler — half in the other
  // world. Swing 3 (Third Strike) crosses a second golden fan
  // over the first: the X that ends the combo.
  const phase2 = step === 2;
  const third = step >= 3;
  const arcCol = phase2 ? pal.main : pal.glow;
  return (
    <group ref={ref} position={[0, 0.6, 0.8]}>
      {[-0.22, 0, 0.22].map((ox, i) => (
        <mesh
          key={i}
          position={[ox * (phase2 ? 1.5 : 1), (1 - Math.abs(i - 1)) * 0.06, 0]}
          rotation={[Math.PI / 2, 0, (i - 1) * 0.32]}
        >
          <torusGeometry args={[0.42, 0.032, 6, 14, Math.PI * 0.85]} />
          <meshBasicMaterial color={arcCol} transparent opacity={phase2 ? 0.55 : 0.9} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {third
        ? [-0.18, 0.18].map((ox, i) => (
            <mesh key={`x${i}`} position={[ox, 0.05, 0.05]} rotation={[Math.PI / 2, 0, (i === 0 ? 1 : -1) * 0.9]}>
              <torusGeometry args={[0.4, 0.035, 6, 14, Math.PI * 0.8]} />
              <meshBasicMaterial color="#ffd24a" transparent opacity={0.95} toneMapped={false} depthWrite={false} />
            </mesh>
          ))
        : null}
      {/* the streak — motion made visible behind the fan */}
      <mesh position={[0, 0, -0.35]} rotation={[0, 0, 0]}>
        <planeGeometry args={[0.5, 0.08]} />
        <meshBasicMaterial color={arcCol} transparent opacity={0.4} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// ─────────────────────────────────────────────────────────────
// FX-1 — SAEL'ESH'S LIVING ARSENAL (Batch FX-1, the Hunt Pass).
// Worn layers mount inside PlayerBody; world-space casts render
// through AbilityWorldFX (exported — zones mount it once in the
// scene and feed it ctrl.lastCast + their world transform).
// All meshBasicMaterial, gaseous rules honored (depthWrite off,
// toneMapped off), fixed phases, ZERO new lights.
// ─────────────────────────────────────────────────────────────
const SHADOW_DARK = "#0b141a"; // the shadow's flesh (display const)
const GRACE_GOLD = "#ffd24a"; //  the King's ember (display const)

// Spectral Paws armed — the front paws burn with ghost-fire while
// the buff waits for its strike.
function PawsFire({ pal }) {
  const grp = useRef();
  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const g = grp.current;
    if (!g) return;
    g.children.forEach((ch, i) => {
      if (ch.userData.wisp) {
        // wisps rise on a loop, fading as they go
        const ph = (t * 0.9 + i * 0.37) % 1;
        ch.position.y = 0.3 + ph * 0.45;
        if (ch.material) ch.material.opacity = 0.35 * (1 - ph);
      } else {
        const breathe = 1 + Math.sin(t * 6 + i * 2.1) * 0.22;
        ch.scale.setScalar(breathe);
      }
    });
  });
  return (
    <group ref={grp}>
      {[-0.16, 0.16].map((ox, i) => (
        <mesh key={i} position={[ox, 0.26, 0.58]}>
          <sphereGeometry args={[0.085, 8, 8]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.85} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      {[0, 1, 2].map((i) => (
        <mesh key={`w${i}`} position={[(i - 1) * 0.14, 0.3, 0.55]} userData={{ wisp: true }}>
          <sphereGeometry args={[0.045, 6, 6]} />
          <meshBasicMaterial color={pal.main} transparent opacity={0.3} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

// Ghost Form worn — a breathing spectral veil + fast wisps. The
// flesh itself thins via PlayerBody's traverse; this is the vapor
// the Tiger wears between worlds.
function GhostVeil({ pal }) {
  const shell = useRef();
  const orbit = useRef();
  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;
    if (shell.current?.material) shell.current.material.opacity = 0.1 + Math.sin(t * 3.2) * 0.04;
    if (orbit.current) orbit.current.rotation.y += dt * 2.2;
  });
  return (
    <group>
      <mesh ref={shell} position={[0, 0.55, 0]}>
        <sphereGeometry args={[0.92, 14, 12]} />
        <meshBasicMaterial color={pal.main} transparent opacity={0.12} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      <group ref={orbit} position={[0, 0.55, 0]}>
        {[0, 1, 2].map((i) => {
          const a = (i / 3) * Math.PI * 2;
          return (
            <mesh key={i} position={[Math.cos(a) * 0.75, Math.sin(a * 2) * 0.15, Math.sin(a) * 0.75]}>
              <sphereGeometry args={[0.05, 6, 6]} />
              <meshBasicMaterial color={pal.glow} transparent opacity={0.5} toneMapped={false} depthWrite={false} />
            </mesh>
          );
        })}
      </group>
    </group>
  );
}

// King's Grace worn — THE aura. A regal double shell and NINE
// ember-lives circling on a tilted ring, each breathing on its
// own fixed phase. When Nine Lives triggers, the armed state
// drops and the aura goes out — a life spent, worn honestly.
function GraceAura({ pal }) {
  const ring = useRef();
  useFrame((state, dt) => {
    const t = state.clock.elapsedTime;
    const r = ring.current;
    if (!r) return;
    r.rotation.y += dt * 0.6;
    r.children.forEach((ch, i) => {
      const breathe = 1 + Math.sin(t * 2 + (i * Math.PI * 2) / 9) * 0.3;
      ch.scale.setScalar(breathe);
    });
  });
  return (
    <group>
      <mesh position={[0, 0.55, 0]}>
        <sphereGeometry args={[0.98, 14, 12]} />
        <meshBasicMaterial color={GRACE_GOLD} transparent opacity={0.06} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      <mesh position={[0, 0.55, 0]}>
        <sphereGeometry args={[1.1, 14, 12]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.045} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      <group ref={ring} position={[0, 0.6, 0]} rotation={[0.35, 0, 0.12]}>
        {Array.from({ length: 9 }).map((_, i) => {
          const a = (i / 9) * Math.PI * 2;
          return (
            <mesh key={i} position={[Math.cos(a) * 0.82, 0, Math.sin(a) * 0.82]}>
              <sphereGeometry args={[0.042, 6, 6]} />
              <meshBasicMaterial color={i % 2 ? GRACE_GOLD : pal.glow} transparent opacity={0.85} toneMapped={false} depthWrite={false} />
            </mesh>
          );
        })}
      </group>
    </group>
  );
}

// ─────────────────────────────────────────────────────────────
// ABILITY WORLD FX (FX-1) — the projected half of a cast, in
// WORLD space. Zones mount <AbilityWorldFX cast={ctrl.lastCast}
// world={world} /> once inside their scene; each new cast id
// remounts the matching effect, which runs its own clock and
// fades itself out. Unknown ability ids are a silent no-op —
// the AbilityFX registry law, honored in 3D.
// ─────────────────────────────────────────────────────────────
export function AbilityWorldFX({ cast, world }) {
  if (!cast || !world) return null;
  const k = cast.id;
  switch (cast.abilityId) {
    case "spectral_claws": return <SpectralClaws3D key={k} ev={cast} world={world} />;
    case "haunting_presence": return <HauntingPresence3D key={k} ev={cast} world={world} />;
    case "nine_lives": return <NineLives3D key={k} ev={cast} world={world} />;
    case "ghost_form": return <GhostBurst3D key={k} ev={cast} world={world} />;
    case "spectral_paws": return <PawsRipple3D key={k} ev={cast} world={world} />;
    default: return null;
  }
}

// The Shadow — Sael'esh's silhouette, built as a silhouette
// SHOULD be: dark overlapping masses and two burning eyes.
// (Attachment law: the eyes sit ON the head — nothing hangs.)
function ShadowTiger({ pal, opacity = 0.55 }) {
  const m = (o) => (
    <meshBasicMaterial color={SHADOW_DARK} transparent opacity={opacity * o} toneMapped={false} depthWrite={false} />
  );
  return (
    <group scale={[1.15, 1.15, 1.15]}>
      <mesh position={[0, 0.42, 0]} scale={[0.5, 0.4, 0.85]}><sphereGeometry args={[1, 12, 10]} />{m(1)}</mesh>
      <mesh position={[0, 0.5, 0.42]} scale={[0.4, 0.36, 0.4]}><sphereGeometry args={[1, 10, 10]} />{m(1)}</mesh>
      <mesh position={[0, 0.74, 0.66]} scale={[0.3, 0.28, 0.3]}><sphereGeometry args={[1, 10, 10]} />{m(1)}</mesh>
      {[-0.14, 0.14].map((ox, i) => (
        <mesh key={i} position={[ox, 0.98, 0.62]} rotation={[0.2, 0, ox > 0 ? -0.25 : 0.25]}>
          <coneGeometry args={[0.09, 0.2, 6]} />{m(0.95)}
        </mesh>
      ))}
      {[-0.11, 0.11].map((ox, i) => (
        <mesh key={`e${i}`} position={[ox, 0.78, 0.93]} rotation={[0, ox > 0 ? 0.25 : -0.25, 0]}>
          <circleGeometry args={[0.05, 10]} />
          <meshBasicMaterial color={pal.glow} toneMapped={false} depthWrite={false} transparent opacity={Math.min(1, opacity * 1.9)} />
        </mesh>
      ))}
      {[0, 1, 2].map((i) => (
        <mesh key={`t${i}`} position={[0, 0.5 + i * 0.16, -0.75 - i * 0.12]} scale={0.11 - i * 0.02}>
          <sphereGeometry args={[1, 8, 8]} />{m(0.9)}
        </mesh>
      ))}
    </group>
  );
}

// SPECTRAL CLAWS — the showpiece. The Shadow leaps OUT of the
// Tiger ("I wanna see the shadow strike with me"), dissolves at
// the apex, and three phantom claws streak from it to the three
// marked prey, bursting into rakes on arrival.
function SpectralClaws3D({ ev, world }) {
  const grp = useRef();
  const shadow = useRef();
  const claws = useRef([]);
  const bursts = useRef([]);
  const t = useRef(0);
  const TTL = 1.4;

  const O = useMemo(() => new THREE.Vector3(...world(ev.origin.x, ev.origin.y)), [ev, world]);
  const T = useMemo(() => {
    const list = (ev.targets || []).map((tp) => new THREE.Vector3(...world(tp.pos.x, tp.pos.y)));
    // a whiffed cast still hunts — the Shadow lunges down the facing
    if (!list.length) {
      const f = ev.facing || { x: 0, y: 1 };
      list.push(new THREE.Vector3(...world(ev.origin.x + f.x * 3.2, ev.origin.y + f.y * 3.2)));
    }
    return list;
  }, [ev, world]);
  const C = useMemo(() => T.reduce((v, p) => v.add(p), new THREE.Vector3()).multiplyScalar(1 / T.length), [T]);
  const launch = useMemo(() => new THREE.Vector3().lerpVectors(O, C, 0.45), [O, C]);

  useFrame((_, dt) => {
    t.current = Math.min(TTL, t.current + dt);
    const s = t.current;
    if (grp.current) grp.current.visible = s < TTL;

    // Phase A (0 → 0.45s): the Shadow's leap — up-and-out arc
    if (shadow.current) {
      const pA = Math.min(1, s / 0.45);
      shadow.current.position.lerpVectors(O, launch, pA);
      shadow.current.position.y = Math.sin(pA * Math.PI) * 1.1;
      shadow.current.lookAt(C.x, 0.4, C.z);
      shadow.current.scale.setScalar(1 + pA * 0.25);
      // dissolve through Phase B
      const fade = s < 0.45 ? 1 : Math.max(0, 1 - (s - 0.45) / 0.3);
      shadow.current.traverse((ch) => {
        if (ch.material) ch.material.opacity = (ch.userData._o ?? (ch.userData._o = ch.material.opacity)) * fade;
      });
      shadow.current.visible = fade > 0.01;
    }

    // Phase B (0.35 → 0.8s): three claws streak to the prey
    claws.current.forEach((cl, i) => {
      if (!cl) return;
      const tgt = T[i % T.length];
      const p = Math.min(1, Math.max(0, (s - 0.35 - i * 0.05) / 0.4));
      const ease = p * p; // ease-in — the claws accelerate
      cl.position.lerpVectors(launch, tgt, ease);
      cl.position.y = 0.7 + Math.sin(p * Math.PI) * 0.4;
      cl.lookAt(tgt.x, 0.5, tgt.z);
      cl.rotation.z += 0.2;
      cl.visible = p > 0 && p < 1;
    });

    // Phase C (arrival → +0.5s): rake bursts at each target
    bursts.current.forEach((b, i) => {
      if (!b) return;
      const arrive = 0.75 + i * 0.05;
      const p = Math.min(1, Math.max(0, (s - arrive) / 0.5));
      b.visible = p > 0 && p < 1;
      const punch = Math.sin(p * Math.PI);
      b.scale.setScalar(0.5 + p * 1.3);
      b.traverse((ch) => {
        if (ch.material) ch.material.opacity = (ch.userData._o ?? (ch.userData._o = ch.material.opacity)) * punch;
      });
    });
  });

  return (
    <group ref={grp}>
      <group ref={shadow} position={O.toArray()}>
        <ShadowTiger pal={getBeingPalette("spectral_tiger")} />
      </group>
      {[0, 1, 2].map((i) => (
        <group key={`c${i}`} ref={(el) => (claws.current[i] = el)} visible={false}>
          {[-0.09, 0, 0.09].map((ox, j) => (
            <mesh key={j} position={[ox, 0, 0]} rotation={[0, 0, (j - 1) * 0.22]}>
              <planeGeometry args={[0.05, 0.55]} />
              <meshBasicMaterial color={getBeingPalette("spectral_tiger").glow} transparent opacity={0.9} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
            </mesh>
          ))}
          <mesh position={[0, 0, -0.35]}>
            <sphereGeometry args={[0.09, 6, 6]} />
            <meshBasicMaterial color={SHADOW_DARK} transparent opacity={0.5} toneMapped={false} depthWrite={false} />
          </mesh>
        </group>
      ))}
      {T.map((p, i) => (
        <group key={`b${i}`} ref={(el) => (bursts.current[i] = el)} position={[p.x, 0.7, p.z]} visible={false}>
          {[-0.5, 0.5].map((rz, j) => (
            <mesh key={j} rotation={[0, 0, rz]}>
              <planeGeometry args={[0.08, 1.1]} />
              <meshBasicMaterial color={getBeingPalette("spectral_tiger").glow} transparent opacity={0.9} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
            </mesh>
          ))}
          <mesh>
            <sphereGeometry args={[0.32, 8, 8]} />
            <meshBasicMaterial color={SHADOW_DARK} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.55, 0]}>
            <ringGeometry args={[0.25, 0.4, 20]} />
            <meshBasicMaterial color={getBeingPalette("spectral_tiger").main} transparent opacity={0.6} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// HAUNTING PRESENCE — HER move, now his. The field goes
// monochrome under a shadow dome, a shockwave rolls outward,
// and a claw-glyph ignites over every enemy at once.
function HauntingPresence3D({ ev, world }) {
  const grp = useRef();
  const dome = useRef();
  const ring1 = useRef();
  const ring2 = useRef();
  const glyphs = useRef([]);
  const t = useRef(0);
  const TTL = 2.2;
  const O = useMemo(() => new THREE.Vector3(...world(ev.origin.x, ev.origin.y)), [ev, world]);
  const T = useMemo(() => (ev.targets || []).map((tp) => new THREE.Vector3(...world(tp.pos.x, tp.pos.y))), [ev, world]);
  const pal = getBeingPalette("spectral_tiger");

  useFrame((_, dt) => {
    t.current = Math.min(TTL, t.current + dt);
    const s = t.current;
    if (grp.current) grp.current.visible = s < TTL;
    // the monochrome takeover — in fast, linger, out slow
    if (dome.current?.material) {
      const inP = Math.min(1, s / 0.35);
      const outP = Math.max(0, (s - 1.4) / 0.8);
      dome.current.material.opacity = 0.3 * inP * (1 - outP);
    }
    const wave = (r, delay, top) => {
      if (!r.current) return;
      const p = Math.min(1, Math.max(0, (s - delay) / 1.0));
      r.current.scale.setScalar(0.2 + p * top);
      if (r.current.material) r.current.material.opacity = 0.75 * (1 - p);
      r.current.visible = p > 0 && p < 1;
    };
    wave(ring1, 0, 13);
    wave(ring2, 0.18, 13);
    glyphs.current.forEach((g, i) => {
      if (!g) return;
      const p = Math.min(1, Math.max(0, (s - 0.25 - i * 0.06) / 0.7));
      g.visible = p > 0 && p < 1;
      const punch = Math.sin(p * Math.PI);
      g.scale.y = 0.3 + p * 1.2;
      g.traverse((ch) => {
        if (ch.material) ch.material.opacity = (ch.userData._o ?? (ch.userData._o = ch.material.opacity)) * punch;
      });
    });
  });

  return (
    <group ref={grp}>
      <mesh ref={dome} position={[O.x, 0, O.z]}>
        <sphereGeometry args={[16, 16, 12]} />
        <meshBasicMaterial color="#0a0e14" transparent opacity={0} side={THREE.BackSide} toneMapped={false} depthWrite={false} />
      </mesh>
      {[ring1, ring2].map((r, i) => (
        <mesh key={i} ref={r} rotation={[-Math.PI / 2, 0, 0]} position={[O.x, 0.08 + i * 0.04, O.z]} visible={false}>
          <ringGeometry args={[0.9, 1, 40]} />
          <meshBasicMaterial color={i ? pal.main : pal.glow} transparent opacity={0.75} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {T.map((p, i) => (
        <group key={`g${i}`} ref={(el) => (glyphs.current[i] = el)} position={[p.x, 0.9, p.z]} visible={false}>
          {[-0.12, 0, 0.12].map((ox, j) => (
            <mesh key={j} position={[ox, 0, 0]} rotation={[0, 0, (j - 1) * 0.12]}>
              <planeGeometry args={[0.06, 1.0]} />
              <meshBasicMaterial color={pal.glow} transparent opacity={0.9} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
            </mesh>
          ))}
          <mesh position={[0, -0.35, 0]}>
            <sphereGeometry args={[0.24, 8, 8]} />
            <meshBasicMaterial color={SHADOW_DARK} transparent opacity={0.45} toneMapped={false} depthWrite={false} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// NINE LIVES — the King's Grace spent. The Tiger dissolves at
// the killing blow (nine embers scatter) and reforms in a column
// of grace at the nearest shadow.
function NineLives3D({ ev, world }) {
  const grp = useRef();
  const embers = useRef([]);
  const ringOut = useRef();
  const column = useRef();
  const ringIn = useRef();
  const t = useRef(0);
  const TTL = 1.6;
  const O = useMemo(() => new THREE.Vector3(...world(ev.origin.x, ev.origin.y)), [ev, world]);
  const R = useMemo(
    () => new THREE.Vector3(...world((ev.reformAt || ev.origin).x, (ev.reformAt || ev.origin).y)),
    [ev, world]
  );
  const pal = getBeingPalette("spectral_tiger");

  useFrame((_, dt) => {
    t.current = Math.min(TTL, t.current + dt);
    const s = t.current;
    if (grp.current) grp.current.visible = s < TTL;
    // dissolve: embers scatter up-and-out from the death spot
    embers.current.forEach((e, i) => {
      if (!e) return;
      const p = Math.min(1, s / 0.7);
      const a = (i / 9) * Math.PI * 2;
      e.position.set(O.x + Math.cos(a) * p * 1.3, 0.6 + p * 1.4 + Math.sin(a * 3) * 0.15, O.z + Math.sin(a) * p * 1.3);
      if (e.material) e.material.opacity = 0.95 * (1 - p);
      e.visible = p < 1;
    });
    const ring = (r, delay, top) => {
      if (!r.current) return;
      const p = Math.min(1, Math.max(0, (s - delay) / 0.6));
      r.current.scale.setScalar(0.2 + p * top);
      if (r.current.material) r.current.material.opacity = 0.8 * (1 - p);
      r.current.visible = p > 0 && p < 1;
    };
    ring(ringOut, 0, 2.4);
    ring(ringIn, 0.35, 2.0);
    // the reform column — grace pouring down where he returns
    if (column.current) {
      const p = Math.min(1, Math.max(0, (s - 0.35) / 0.8));
      const punch = Math.sin(p * Math.PI);
      column.current.visible = p > 0 && p < 1;
      if (column.current.material) column.current.material.opacity = 0.5 * punch;
      column.current.scale.set(1, 0.4 + p * 0.8, 1);
    }
  });

  return (
    <group ref={grp}>
      {Array.from({ length: 9 }).map((_, i) => (
        <mesh key={i} ref={(el) => (embers.current[i] = el)} visible={false}>
          <sphereGeometry args={[0.055, 6, 6]} />
          <meshBasicMaterial color={i % 2 ? GRACE_GOLD : pal.glow} transparent opacity={0.95} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
      <mesh ref={ringOut} rotation={[-Math.PI / 2, 0, 0]} position={[O.x, 0.08, O.z]} visible={false}>
        <ringGeometry args={[0.7, 0.85, 28]} />
        <meshBasicMaterial color={GRACE_GOLD} transparent opacity={0.8} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh ref={column} position={[R.x, 1.4, R.z]} visible={false}>
        <cylinderGeometry args={[0.42, 0.55, 2.8, 12, 1, true]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh ref={ringIn} rotation={[-Math.PI / 2, 0, 0]} position={[R.x, 0.08, R.z]} visible={false}>
        <ringGeometry args={[0.7, 0.85, 28]} />
        <meshBasicMaterial color={pal.glow} transparent opacity={0.8} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// GHOST FORM — the crossing. A shell blooms outward and wisps
// rise as the Tiger steps halfway out of the world.
function GhostBurst3D({ ev, world }) {
  const grp = useRef();
  const shell = useRef();
  const wisps = useRef([]);
  const t = useRef(0);
  const TTL = 1.1;
  const O = useMemo(() => new THREE.Vector3(...world(ev.origin.x, ev.origin.y)), [ev, world]);
  const pal = getBeingPalette("spectral_tiger");
  useFrame((_, dt) => {
    t.current = Math.min(TTL, t.current + dt);
    const s = t.current;
    if (grp.current) grp.current.visible = s < TTL;
    if (shell.current) {
      const p = Math.min(1, s / 0.8);
      shell.current.scale.setScalar(0.4 + p * 1.8);
      if (shell.current.material) shell.current.material.opacity = 0.35 * (1 - p);
    }
    wisps.current.forEach((w, i) => {
      if (!w) return;
      const p = Math.min(1, Math.max(0, (s - i * 0.08) / 0.8));
      const a = (i / 6) * Math.PI * 2;
      w.position.set(O.x + Math.cos(a) * 0.5, 0.3 + p * 1.6, O.z + Math.sin(a) * 0.5);
      if (w.material) w.material.opacity = 0.55 * (1 - p);
      w.visible = p > 0 && p < 1;
    });
  });
  return (
    <group ref={grp}>
      <mesh ref={shell} position={[O.x, 0.7, O.z]}>
        <sphereGeometry args={[0.8, 12, 10]} />
        <meshBasicMaterial color={pal.main} transparent opacity={0.35} toneMapped={false} depthWrite={false} />
      </mesh>
      {Array.from({ length: 6 }).map((_, i) => (
        <mesh key={i} ref={(el) => (wisps.current[i] = el)} visible={false}>
          <sphereGeometry args={[0.06, 6, 6]} />
          <meshBasicMaterial color={pal.glow} transparent opacity={0.55} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

// SPECTRAL PAWS — the coil. A ground ripple where the paws
// strike sparks, and dark smoke curls off the ignition.
function PawsRipple3D({ ev, world }) {
  const grp = useRef();
  const ring = useRef();
  const puffs = useRef([]);
  const t = useRef(0);
  const TTL = 0.85;
  const O = useMemo(() => new THREE.Vector3(...world(ev.origin.x, ev.origin.y)), [ev, world]);
  const pal = getBeingPalette("spectral_tiger");
  useFrame((_, dt) => {
    t.current = Math.min(TTL, t.current + dt);
    const s = t.current;
    if (grp.current) grp.current.visible = s < TTL;
    if (ring.current) {
      const p = Math.min(1, s / 0.7);
      ring.current.scale.setScalar(0.3 + p * 1.6);
      if (ring.current.material) ring.current.material.opacity = 0.65 * (1 - p);
    }
    puffs.current.forEach((w, i) => {
      if (!w) return;
      const p = Math.min(1, Math.max(0, (s - i * 0.07) / 0.6));
      w.position.y = 0.15 + p * 0.7;
      w.scale.setScalar(0.6 + p * 0.9);
      if (w.material) w.material.opacity = 0.4 * (1 - p);
      w.visible = p > 0 && p < 1;
    });
  });
  return (
    <group ref={grp}>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} position={[O.x, 0.06, O.z]}>
        <ringGeometry args={[0.45, 0.58, 24]} />
        <meshBasicMaterial color={pal.main} transparent opacity={0.65} toneMapped={false} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} ref={(el) => (puffs.current[i] = el)} position={[O.x + (i - 1.5) * 0.16, 0.15, O.z + 0.3]} visible={false}>
          <sphereGeometry args={[0.09, 6, 6]} />
          <meshBasicMaterial color={SHADOW_DARK} transparent opacity={0.4} toneMapped={false} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// ENEMY BODIES — keyed to family + role, on-canon per the
// taxonomy's `visual` descriptors. Seeded per id so each instance
// is stable. Telegraph flashes are applied by the caller (tint).
//   beast     → fur/fangs/claws/feral (wolf/fox silhouettes)
//   corrupted → twisted, darker, antlered
//   spectral  → translucent, phased
//   machine   → clockwork/brass
//   construct → stone
// role scales overall size (mook < skirmisher < brute < elite < boss).
// ────────────────────────────────────────────────────────────
const ROLE_SCALE = { mook: 0.8, skirmisher: 0.75, brute: 1.15, caster: 0.85, summoner: 0.9, elite: 1.4, subboss: 1.7, world_boss: 2.4 };
const roleScale = (role) => ROLE_SCALE[role] || 0.9;

const FAMILY_COLOR = {
  beast: { main: "#7a5a3a", accent: "#3a2a1a", eye: "#ffcf5c" },
  corrupted: { main: "#4a2a3a", accent: "#8a2a4a", eye: "#ff5c8a" },
  spectral: { main: "#6a8a9a", accent: "#9fe8ff", eye: "#bfe8ff" },
  machine: { main: "#8a7a4a", accent: "#4ae8ff", eye: "#4ae8ff" },
  construct: { main: "#6a6258", accent: "#3a352e", eye: "#c9a84c" },
  dragon: { main: "#8a3a2a", accent: "#ff7a3a", eye: "#ff9b2e" },
  humanoid: { main: "#5a5a6a", accent: "#8a93a8", eye: "#bfe0ff" },
  nihil_born: { main: "#2a1a3a", accent: "#a04aff", eye: "#c94aff" },
};
export const familyColor = (fam) => FAMILY_COLOR[fam] || FAMILY_COLOR.beast;

export function EnemyMesh({ family = "beast", role = "mook", id = 0, phased = false, telegraph = false, facing = { x: 0, y: 1 } }) {
  const c = familyColor(family);
  const s = roleScale(role);
  const R = mulberry32(hashId(id) + 3);
  const yaw = Math.atan2(facing.x || 0, facing.y || 1);
  const opacity = phased ? 0.28 : (family === "spectral" ? 0.7 : 1);
  const tel = telegraph ? "#ff5a3a" : c.accent;

  // Quadruped beast/corrupted body (wolf, fox, elk, antlered)
  const isQuad = family === "beast" || family === "corrupted";
  const isElk = role === "brute" || role === "elite";

  if (isQuad) {
    return (
      <group rotation={[0, yaw, 0]} scale={[s, s, s]}>
        {/* body */}
        <mesh position={[0, 0.34, 0]} castShadow><boxGeometry args={[0.42, 0.36, 0.95]} /><meshStandardMaterial color={c.main} roughness={0.9} transparent={opacity < 1} opacity={opacity} emissive={telegraph ? "#ff3a1a" : "#000"} emissiveIntensity={telegraph ? 0.5 : 0} /></mesh>
        {/* head */}
        <mesh position={[0, 0.44, 0.55]} castShadow><boxGeometry args={[0.34, 0.32, 0.36]} /><meshStandardMaterial color={c.main} transparent={opacity < 1} opacity={opacity} /></mesh>
        {/* snout */}
        <mesh position={[0, 0.38, 0.78]}><boxGeometry args={[0.18, 0.16, 0.18]} /><meshStandardMaterial color={c.accent} /></mesh>
        {/* ears / antlers */}
        {isElk ? (
          [-0.14, 0.14].map((ox, i) => (
            <group key={i} position={[ox, 0.62, 0.5]}>
              <mesh rotation={[0.2, 0, ox > 0 ? -0.3 : 0.3]}><coneGeometry args={[0.04, 0.4, 4]} /><meshStandardMaterial color={c.accent} /></mesh>
              <mesh position={[ox > 0 ? 0.1 : -0.1, 0.18, 0]}><coneGeometry args={[0.03, 0.22, 4]} /><meshStandardMaterial color={c.accent} /></mesh>
            </group>
          ))
        ) : (
          [-0.11, 0.11].map((ox, i) => (
            <mesh key={i} position={[ox, 0.62, 0.5]}><coneGeometry args={[0.06, 0.16, 4]} /><meshStandardMaterial color={c.accent} /></mesh>
          ))
        )}
        {/* eyes */}
        <mesh position={[-0.09, 0.46, 0.74]}><sphereGeometry args={[0.04, 6, 6]} /><meshBasicMaterial color={tel} toneMapped={false} /></mesh>
        <mesh position={[0.09, 0.46, 0.74]}><sphereGeometry args={[0.04, 6, 6]} /><meshBasicMaterial color={tel} toneMapped={false} /></mesh>
        {/* legs */}
        {[[-0.15, 0.32], [0.15, 0.32], [-0.15, -0.32], [0.15, -0.32]].map(([ox, oz], i) => (
          <mesh key={i} position={[ox, 0.13, oz]}><boxGeometry args={[0.08, 0.28, 0.08]} /><meshStandardMaterial color={c.accent} transparent={opacity < 1} opacity={opacity} /></mesh>
        ))}
        {/* tail */}
        <mesh position={[0, 0.4, -0.55]} rotation={[0.5, 0, 0]}><cylinderGeometry args={[0.05, 0.02, 0.5, 5]} /><meshStandardMaterial color={c.main} transparent={opacity < 1} opacity={opacity} /></mesh>
        {telegraph && <pointLight position={[0, 0.5, 0]} color="#ff5a3a" intensity={1.5} distance={3} />}
      </group>
    );
  }

  // Spectral biped (prowler, Matriarch) — translucent floating shade
  if (family === "spectral") {
    return (
      <group rotation={[0, yaw, 0]} scale={[s, s, s]}>
        {/* tapered shroud */}
        <mesh position={[0, 0.7, 0]} castShadow><coneGeometry args={[0.45, 1.5, 10]} /><meshStandardMaterial color={c.main} roughness={1} transparent opacity={opacity} emissive={telegraph ? "#ff3a1a" : c.accent} emissiveIntensity={telegraph ? 0.6 : 0.15} /></mesh>
        {/* head */}
        <mesh position={[0, 1.4, 0]}><sphereGeometry args={[0.24, 12, 12]} /><meshStandardMaterial color={c.main} transparent opacity={opacity} /></mesh>
        {/* hollow eyes */}
        <mesh position={[-0.09, 1.42, 0.19]}><sphereGeometry args={[0.045, 6, 6]} /><meshBasicMaterial color={tel} toneMapped={false} /></mesh>
        <mesh position={[0.09, 1.42, 0.19]}><sphereGeometry args={[0.045, 6, 6]} /><meshBasicMaterial color={tel} toneMapped={false} /></mesh>
        {/* wispy arms */}
        {[-0.4, 0.4].map((ox, i) => (
          <mesh key={i} position={[ox, 0.9, 0.1]} rotation={[0, 0, ox > 0 ? -0.5 : 0.5]}><cylinderGeometry args={[0.05, 0.02, 0.6, 5]} /><meshStandardMaterial color={c.main} transparent opacity={opacity * 0.8} /></mesh>
        ))}
        {/* under-glow */}
        <pointLight position={[0, 0.9, 0]} color={telegraph ? "#ff5a3a" : c.accent} intensity={phased ? 0.4 : 1.1} distance={4} />
      </group>
    );
  }

  // Machine biped (clockwork)
  if (family === "machine") {
    return (
      <group rotation={[0, yaw, 0]} scale={[s, s, s]}>
        <mesh position={[0, 0.6, 0]} castShadow><boxGeometry args={[0.5, 0.9, 0.4]} /><meshStandardMaterial color={c.main} metalness={0.6} roughness={0.4} emissive={telegraph ? "#ff3a1a" : "#000"} emissiveIntensity={telegraph ? 0.5 : 0} /></mesh>
        <mesh position={[0, 1.2, 0]} castShadow><boxGeometry args={[0.4, 0.36, 0.36]} /><meshStandardMaterial color={c.accent} metalness={0.5} /></mesh>
        <mesh position={[0, 1.22, 0.19]}><boxGeometry args={[0.3, 0.1, 0.02]} /><meshBasicMaterial color={tel} toneMapped={false} /></mesh>
        {telegraph && <pointLight position={[0, 1, 0]} color="#ff5a3a" intensity={1.5} distance={3} />}
      </group>
    );
  }

  // Construct / fallback — stone hulk
  return (
    <group rotation={[0, yaw, 0]} scale={[s, s, s]}>
      <mesh position={[0, 0.6, 0]} castShadow><boxGeometry args={[0.6, 1.0, 0.5]} /><meshStandardMaterial color={c.main} roughness={1} emissive={telegraph ? "#ff3a1a" : "#000"} emissiveIntensity={telegraph ? 0.5 : 0} /></mesh>
      <mesh position={[0, 1.25, 0]} castShadow><boxGeometry args={[0.4, 0.4, 0.4]} /><meshStandardMaterial color={c.accent} roughness={1} /></mesh>
      <mesh position={[-0.09, 1.28, 0.19]}><sphereGeometry args={[0.05, 6, 6]} /><meshBasicMaterial color={tel} toneMapped={false} /></mesh>
      <mesh position={[0.09, 1.28, 0.19]}><sphereGeometry args={[0.05, 6, 6]} /><meshBasicMaterial color={tel} toneMapped={false} /></mesh>
      {telegraph && <pointLight position={[0, 1, 0]} color="#ff5a3a" intensity={1.5} distance={3} />}
    </group>
  );
}

// ────────────────────────────────────────────────────────────
// CAMERAS — two archetypes.
//  FollowRig: over-the-shoulder trail (fields/exploration). Goal-
//   based lerp, user always wins on drag, polar clamp. Pass a
//   followRef (THREE.Vector3, updated each frame by the caller).
//  StageRig: fixed cinematic mark that gently tracks the actor's
//   XZ but never moves the camera position — for enclosed rooms
//   (the crypt). No orbit at all → no camera bugs.
// ────────────────────────────────────────────────────────────
export function FollowRig({ followRef, trail = [0.001, 12, 15], look = [0, 1.2, 0], min = 5, max = 90 }) {
  const controls = useRef();
  const { camera } = useThree();
  const goal = useRef({ pos: new THREE.Vector3(), tgt: new THREE.Vector3(), active: true });
  useFrame(() => {
    const g = goal.current;
    if (followRef.current) {
      const p = followRef.current;
      g.pos.set(p.x + trail[0], trail[1], p.z + trail[2]);
      g.tgt.set(p.x + look[0], look[1], p.z + look[2]);
      g.active = true;
    }
    if (g.active && controls.current) {
      camera.position.lerp(g.pos, 0.06);
      controls.current.target.lerp(g.tgt, 0.08);
      controls.current.update();
    }
  });
  return (
    <OrbitControls
      ref={controls} enableDamping dampingFactor={0.08}
      minDistance={min} maxDistance={max} maxPolarAngle={Math.PI / 2.12}
      onStart={() => { /* follow mode: let them orbit but re-lock next frame */ }}
    />
  );
}

// StageRig: the camera stands at a fixed mark and only re-aims at
// the actor (a tripod pan, not a move). No OrbitControls → the
// shot cannot break. `mark` and `aimHeight` are solved by the room.
export function StageRig({ mark = [0, 9, 14], aimRef, aimHeight = 1.2, lerp = 0.09 }) {
  const { camera } = useThree();
  const aim = useRef(new THREE.Vector3(0, aimHeight, 0));
  useFrame(() => {
    camera.position.set(mark[0], mark[1], mark[2]);
    if (aimRef && aimRef.current) {
      aim.current.lerp(new THREE.Vector3(aimRef.current.x, aimHeight, aimRef.current.z), lerp);
    }
    camera.lookAt(aim.current);
  });
  return null;
}

// ────────────────────────────────────────────────────────────
// TOUCH CONTROLS — plain DOM (renders OUTSIDE <Canvas>). Writes
// the SAME keysRef the controller/player reads, so mobile drive
// is free on all three 3D screens. Attack/dodge/interact fire the
// optional callbacks (the zones already bind these to the desktop
// keys — pass the same handlers). Only shows on touch devices.
// ────────────────────────────────────────────────────────────
export const isTouch = () =>
  typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

export function TouchControls({ keysRef, onAttack = null, onDodge = null, onInteract = null, accent = "#c9a84c" }) {
  if (!isTouch()) return null;
  const set = (k, v) => (e) => { e.preventDefault(); if (keysRef?.current) keysRef.current[k] = v; };
  const dpadBtn = (label, k, gx, gy) => (
    <button
      onTouchStart={set(k, true)} onTouchEnd={set(k, false)} onTouchCancel={set(k, false)}
      onMouseDown={set(k, true)} onMouseUp={set(k, false)} onMouseLeave={set(k, false)}
      style={{ ...padKey, gridColumn: gx, gridRow: gy, borderColor: accent }}
    >{label}</button>
  );
  const actBtn = (label, fn, col) => (
    <button
      onTouchStart={(e) => { e.preventDefault(); fn && fn(); }}
      onMouseDown={(e) => { e.preventDefault(); fn && fn(); }}
      style={{ ...actKey, borderColor: col }}
    >{label}</button>
  );
  return (
    <>
      {/* left: d-pad */}
      <div style={dpadWrap}>
        {dpadBtn("▲", "up", 2, 1)}
        {dpadBtn("◀", "left", 1, 2)}
        {dpadBtn("▶", "right", 3, 2)}
        {dpadBtn("▼", "down", 2, 3)}
      </div>
      {/* right: actions */}
      <div style={actWrap}>
        {onAttack && actBtn("⚔", onAttack, "#e86a5c")}
        {onDodge && actBtn("↻", onDodge, "#8fb8d8")}
        {onInteract && actBtn("✦", onInteract, accent)}
      </div>
    </>
  );
}

const padKey = {
  width: 52, height: 52, fontSize: 20, color: "#e9ddc4",
  background: "rgba(20,16,25,0.82)", border: "2px solid #c9a84c", borderRadius: 12,
  display: "flex", alignItems: "center", justifyContent: "center",
  userSelect: "none", touchAction: "none", WebkitTapHighlightColor: "transparent",
};
const dpadWrap = {
  position: "absolute", left: 18, bottom: 24, zIndex: 30,
  display: "grid", gridTemplateColumns: "repeat(3, 52px)", gridTemplateRows: "repeat(3, 52px)", gap: 4,
};
const actKey = {
  width: 62, height: 62, fontSize: 24, color: "#e9ddc4",
  background: "rgba(20,16,25,0.82)", border: "2px solid #e86a5c", borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
  userSelect: "none", touchAction: "none", WebkitTapHighlightColor: "transparent",
};
const actWrap = {
  position: "absolute", right: 18, bottom: 24, zIndex: 30,
  display: "flex", flexDirection: "column-reverse", gap: 12, alignItems: "center",
};
